import type { BrowserInfo } from '@adyen/adyen-web';
import { CardElement } from '@stripe/react-stripe-js';
import type {
  PaymentIntentResult,
  Stripe,
  StripeCardElement,
  StripeCardElementOptions,
  StripeElements,
} from '@stripe/stripe-js';
import { AxiosError } from 'axios';
import { hoursToSeconds, millisecondsToSeconds } from 'date-fns';
import { t } from 'i18next';
import { isEmpty } from 'lodash-es';
import isNumber from 'lodash-es/isNumber.js';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';
import type { NavigateFunction } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import {
  DeliveryMethods,
  type DeliverySlot,
  type DeliverySlotsResponse,
  ETAPaymentMethod,
  isPhonePaymentMethod,
  isSettledOfflineMethod,
} from '~/api/ETA';
import {
  isSettledOfflineSystem,
  type NewOrderResponse,
  type Payment,
  PaymentSystem,
} from '~/api/Order';
import type { ApiErrorResponse } from '~/api/Requests';
import UnknownCard from '~/assets/img/unknown_card.png';
import { company } from '~/company/Company';
import { getAgeByDate } from '~/components/ModalBirthDate/utils';
import CARD_BRANDS from '~/pages/Checkout/cardBrands';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { trackPurchase } from '~/services/analytics/purchaseTracking';
import eventEmitter, { EventsName } from '~/services/Events';
import {
  type PurchaseSnapshot,
  purchaseRecovery,
} from '~/services/purchaseRecovery';
import {
  checkoutAttempt,
  DOMAIN,
  REASON,
  reportError,
  reportFailure,
  reportInfo,
  reportWarning,
  sanitizeUrl,
  trace,
} from '~/services/telemetry';
import {
  cartStore,
  checkoutStore,
  deliveryMethodAvailabilityStore,
  mainStore,
  orderStore,
  settingStore,
} from '~/stores';
import AdditionalPaymentMethod from '~/stores/CheckoutStore/AdditionalPaymentMethod';
import type Delivery from '~/stores/CheckoutStore/Delivery';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import type OrderData from '~/stores/CheckoutStore/OrderData';
import type PaymentMethod from '~/stores/CheckoutStore/PaymentMethod';
import type StateData from '~/stores/CheckoutStore/StateData';
import { MetaStore } from '~/stores/shared/MetaStore';
import { SlotsChangedEventStore } from '~/stores/shared/SlotChangedEventStore';
import { formatPrice } from '~/utils/formaters';
import { normalizeDeliveryMethod } from '~/utils/normalizeDeliveryMethod';
import { partnerPickupAddressRequest } from '~/utils/partnerPickupAddress';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

import type { CartItem } from '../CartStore/interfaces';
import { isCartItemInWarehouseStock } from '../CartStore/stock';
import {
  PAYMENT_STALL_REPORT_AFTER,
  PAYMENT_TIMEOUT,
  PAYMENT_WAITING_TIME,
  PRESENT_PROMOCODES,
} from '../constants';
import { storage as localStorage } from '../LocalStorage';
import type { RequestETAResponse } from '../OrderStore/OrderStore';
import { OrderErrorHandled } from '../OrderStore/parseOrderError';
import type { UserStore } from '../UserStore';
import type { CheckoutStorePartial } from './interfaces';
import {
  backorderFloorFor,
  filterBookablePickupSlots,
  isSameSlotSelection,
  pickupSlotSelection,
} from './pickupSlots';
import { SlotSelectModalStore } from './SlotSelectModalStore';
import { groupByDeliverySlots, type NormalizedDeliveryItems } from './utils';

export interface AdditionalPaymentData {
  paymentMethodInfo: {
    encryptedCard?: string;
    deviceInfoId?: string;
    paymentMethodDetails?: {
      [key: string]: any;
      checkoutAttemptId?: string | undefined;
    };
    browserInfo?: BrowserInfo;
    origin?: string;
  };
  billingInfo?: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    countryCode: string;
  };
}

const isEmailAccepted = (value: string): boolean =>
  company.isCustomerEmailOptional && !value.trim()
    ? true
    : mainStore.validateEmail(value);

/** Order note for the dispatcher and courier when the customer allows delivery before the slot */
const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const DELIVER_EARLIER_NOTE = 'DELIVERY EARLIER, IF POSSIBLE';

export class CheckoutStore implements CheckoutStorePartial {
  addressVal = '';
  address2Val = '';
  addressFloorVal = '';
  addressBldVal = '';
  addressAptVal = '';
  phoneVal = '';
  isPhoneValid = true;
  isEmailSync = true;
  isPersonalDataSync = true;
  nameVal = '';
  emailVal = '';

  /**
   * Whether the inline external-recipient form (DubaiExpress) currently holds a
   * valid name + email. Defaults to true so companies without the form — and
   * any failure to mount it — never block checkout; the form keeps it in sync.
   */
  isExternalRecipientValid = true;
  commentVal = '';
  recipientPhoneVal = '';

  isRecipientPhoneValid = false;
  recipientNameVal = '';
  recipientEmailVal = '';
  recipientTempPhoneVal = '';
  isRecipientTempPhoneValid = false;
  recipientTempNameVal = '';
  recipientTempEmailVal = '';
  agreeSaveCard = true;
  error: string | null = null;
  isLoading = false;
  isDisabled = true;
  isPersonalDataChanged = false;
  isShowRecipientPopover = false;
  isShowTimeoutPopover = false;
  /**
   * Device geolocation is farther from the selected delivery address than the
   * zone's geofencing threshold. Drives the passive checkout notice; tenants
   * without `showGeofencingNotice` get the modal instead and never set this.
   */
  isFarFromDeliveryAddress = false;
  isUnableConfirmCardPayment = false;
  isAddNewCard = false;
  _isCardValid = false;
  _isCardAdded = false;
  paymentIntent: NewOrderResponse['data']['orders'][number] | null = null;
  paymentIntentMultiorder: NewOrderResponse['data']['multi_order'] | null =
    null;
  paymentMethod: { card: StripeCardElement } | string = '';
  orderId = '';
  /**
   * How many orders the backend created from the cart. Multi-warehouse carts
   * are split into one order per partner warehouse.
   */
  createdOrdersCount = 0;
  octoPaymentLink: string | null = null;
  monetaPaymentLink: string | null = null;
  private _processedOrderIds = new Set<string>();
  cancellationDelay: number | null = null;
  isThreeds2Active = false;
  instructionList: Record<number, string> = {};
  /** Customer allows the courier to bring a slot order before the slot starts */
  deliverEarlier = false;
  activePaymentMethod: PaymentMethod | null = null;
  activePaymentSystem: PaymentSystem | null = null;
  payments: Array<{ value: string; text: string }> | null = null;
  /**
   * Whether {@link payments} reflects a completed lookup. `payments` is `null`
   * both before the first lookup and when the customer has no saved cards, so
   * this flag is the only way to tell "not resolved yet" from "none". Callers
   * that branch on the presence of saved cards must wait for it, otherwise they
   * act on the loading state. Deliberately not persisted: every checkout visit
   * resolves the list again.
   */
  arePaymentsLoaded = false;
  paymentsMethodId: string | null = null;
  cardStyle: StripeCardElementOptions = {
    style: {
      base: {
        color: '#333',
        fontFamily: '"Noto Sans", sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#878786',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };
  stateData: StateData | null = null;
  isRefreshed = false;
  deliveryMethod: DeliveryMethods = DeliveryMethods.COURIER_DELIVERY;
  /**
   * Method the user wants to switch to, held while the "cart will be cleared"
   * confirmation modal is open (delivery switcher only). Applied on confirm,
   * discarded on cancel.
   */
  pendingDeliveryMethod: DeliveryMethods | null = null;
  isUpdatedDeliveryPopover = false;
  deliveryPrices: RequestETAResponse | null = null;
  useBonuses = false;
  sessionId: string | undefined = undefined;

  deliveryAllTogether = false;

  deliverySlots: DeliverySlotsResponse = {};

  isDeliverTogether = company.combineItemsByDefaut;

  hasExactAmount = false;
  changeFromAmount: Nullable<number> = null;

  mobileMoneyPhoneVal = '';

  _isNewAdyenCard: boolean | null = null;
  _isAdyenCardValid: boolean | null = null;

  // FIXME: Why is this a string?
  selectedDeliveryDate: string | null = '0';
  selectedDeliverySlot: DeliverySlot | null = null;
  deliveries: Delivery[] | null = null;
  isMultipleDeliveriesAvailable = false;

  readonly slotsChangedEventStore: SlotsChangedEventStore;
  readonly slotSelectionModalStore: SlotSelectModalStore;
  readonly deliveryMethodChangeMeta: MetaStore = new MetaStore();

  constructor(protected readonly userStore: UserStore) {
    this.slotsChangedEventStore = new SlotsChangedEventStore(this);
    this.slotSelectionModalStore = new SlotSelectModalStore(this);

    makeAutoObservable(this);
    this.migratePersistedDeliveryMethod();

    makePersistable(this, {
      name: 'CheckoutStore',
      properties: [
        'activePaymentMethod',
        'activePaymentSystem',
        'payments',
        'deliveryMethod',
        'paymentsMethodId',
        'stateData',
        'deliveries',
        'isMultipleDeliveriesAvailable',
        'isDeliverTogether',
      ],
      storage: localStorage,
    });
  }

  /**
   * One-time migration: storage written before the ETA v3 rename holds the old
   * delivery method name, which misses every lookup keyed by delivery method
   * (cost, payment methods, zone) and would show a free delivery that is not
   * free.
   *
   * Rewrites the stored value before makePersistable reads it, so the store is
   * never briefly hydrated with a name the rest of the app cannot resolve — an
   * extra render with a stale method would flip which payment components mount.
   *
   * TODO: remove once no stored state can still carry `JIFFY_DELIVERY`, i.e. a
   * couple of releases after this ships.
   */
  private migratePersistedDeliveryMethod(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem('CheckoutStore');

      if (!raw) {
        return;
      }

      const data = JSON.parse(raw);
      const migrated = {
        ...data,
        ...(data.deliveryMethod
          ? { deliveryMethod: normalizeDeliveryMethod(data.deliveryMethod) }
          : {}),
        ...(data.stateData?.deliveryMethod
          ? {
              stateData: {
                ...data.stateData,
                deliveryMethod: normalizeDeliveryMethod(
                  data.stateData.deliveryMethod,
                ),
              },
            }
          : {}),
      };

      window.localStorage.setItem('CheckoutStore', JSON.stringify(migrated));
    } catch {
      // Unreadable storage: makePersistable falls back to the defaults.
    }
  }

  // Getters
  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  get isAwaitingProviderPayment(): boolean {
    return (
      (this.activePaymentSystem === PaymentSystem.octo ||
        this.activePaymentSystem === PaymentSystem.moneta) &&
      !!this.orderId
    );
  }

  get isPersonalDataValid(): boolean {
    return !!(
      this.isPhoneValid &&
      this.userStore.fullName.length &&
      this.isEmailAcceptable &&
      this.isEmailSync &&
      this.isPersonalDataSync
    );
  }

  get isRecipientValid(): boolean {
    return !!(
      this.isPersonalDataValid &&
      this.isRecipientPhoneValid &&
      this.recipientNameVal.length &&
      this.isRecipientEmailAcceptable
    );
  }

  get isRecipientTempValid(): boolean {
    return !!(
      this.isRecipientTempPhoneValid &&
      this.recipientTempNameVal.length &&
      this.isRecipientTempEmailAcceptable
    );
  }

  get isEmailValid(): boolean {
    return mainStore.validateEmail(this.emailVal);
  }

  get isEmailAcceptable(): boolean {
    return isEmailAccepted(this.emailVal);
  }

  get isRecipientEmailValid(): boolean {
    return mainStore.validateEmail(this.recipientEmailVal);
  }

  get isRecipientEmailAcceptable(): boolean {
    return isEmailAccepted(this.recipientEmailVal);
  }

  get isRecipientTempEmailValid(): boolean {
    return mainStore.validateEmail(this.recipientTempEmailVal);
  }

  get isRecipientTempEmailAcceptable(): boolean {
    return isEmailAccepted(this.recipientTempEmailVal);
  }

  get hasAgeRestrictionError(): boolean {
    if (!company.hasAgeRestriction) {
      return false;
    }

    if (
      (!this.userStore.isAuthorized &&
        cartStore.hasRestrictedCategoriesInCart) ||
      cartStore.isMissingBirthdayErrorCatched
    ) {
      return true;
    }

    return (
      this.userStore.isAuthorized &&
      !this.userStore.dateOfBirth &&
      cartStore.hasRestrictedCategoriesInCart
    );
  }

  get ageRestrictionErrorType():
    | 'unauthorized'
    | 'no-date'
    | 'underage'
    | null {
    if (!company.hasAgeRestriction) {
      return null;
    }

    if (
      !this.userStore.isAuthorized &&
      cartStore.hasRestrictedCategoriesInCart
    ) {
      return 'unauthorized';
    }

    if (
      (this.userStore.isAuthorized &&
        !this.userStore.dateOfBirth &&
        cartStore.hasRestrictedCategoriesInCart) ||
      cartStore.isMissingBirthdayErrorCatched
    ) {
      return 'no-date';
    }

    if (
      this.userStore.isAuthorized &&
      this.userStore.dateOfBirth &&
      getAgeByDate(new Date(this.userStore.dateOfBirth)) <
        settingStore.maxMinAge &&
      cartStore.hasRestrictedCategoriesInCart
    ) {
      return 'underage';
    }

    return null;
  }

  get isFormValid(): boolean {
    if (company.hasAddress2 && !this.address2Val.trim()) {
      return false;
    }

    return (
      (this.isPersonalDataValid || this.isRecipientValid) &&
      this.activePaymentMethod !== null &&
      !!this.addressVal.trim()
    );
  }

  get availableDeliveryMethods(): DeliveryMethods[] {
    if (company.showDeliveryMethodSwitcher) {
      return deliveryMethodAvailabilityStore.availableMethods;
    }

    const methodList = Object.keys(
      orderStore.etaSlotCalculation?.cost ||
        orderStore.etaCalculation?.cost ||
        {},
    ) as DeliveryMethods[];
    if (!methodList.length) {
      return [];
    }
    return [
      DeliveryMethods.COURIER_DELIVERY,
      DeliveryMethods.CLICK_AND_COLLECT,
    ].filter((method) => methodList.includes(method));
  }

  get availablePaymentMethods(): PaymentMethod[] {
    let etaMethods =
      orderStore.etaSlotCalculation?.paymentMethods[this.deliveryMethod] ||
      orderStore.etaCalculation?.paymentMethods[this.deliveryMethod] ||
      [];

    if (!etaMethods.length) {
      etaMethods =
        orderStore.etaSlotCalculation?.paymentMethods[
          DeliveryMethods.COURIER_DELIVERY
        ] ||
        orderStore.etaCalculation?.paymentMethods[
          DeliveryMethods.COURIER_DELIVERY
        ] ||
        [];
    }

    const paymentSystemMethods = orderStore.paymentMethodsMap;
    const intersectedMethods = etaMethods.filter(
      (method) =>
        paymentSystemMethods.has(method) &&
        (paymentSystemMethods.get(method)?.length ?? 0) > 0,
    );

    const availablePaymentMethods: PaymentMethod[] = [];

    for (const method of intersectedMethods) {
      availablePaymentMethods.push(method);

      if (method === ETAPaymentMethod.Card) {
        if (mainStore.isApplePay) {
          availablePaymentMethods.push(MobilePaymentMethod.ApplePay);
        }
        if (
          mainStore.isGooglePay &&
          !orderStore.paymentSystems.has(PaymentSystem.myfatoorah)
        ) {
          availablePaymentMethods.push(MobilePaymentMethod.GooglePay);
        }
      }
    }

    // Offered by the payment system alone: the zone's `payment_methods` is a
    // backend enum with no POSTPAYMENT value to advertise it with.
    if (
      orderStore.paymentSystems.has(PaymentSystem.postpayment) &&
      !availablePaymentMethods.includes(ETAPaymentMethod.Postpayment)
    ) {
      availablePaymentMethods.push(ETAPaymentMethod.Postpayment);
    }

    return availablePaymentMethods;
  }

  get sortedDeliverySlotsList(): DeliverySlot[] | null {
    if (isEmpty(this.deliverySlots)) {
      return null;
    }

    const slots = Object.values(this.deliverySlots).flat();

    return [...slots].sort((a, b) => a.delivery_min_time - b.delivery_min_time);
  }

  get isPickupSelected(): boolean {
    return this.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT;
  }

  /** Early delivery is only meaningful for a courier order with an explicitly chosen slot */
  get isDeliverEarlierRequested(): boolean {
    return (
      this.deliverEarlier &&
      !this.isPickupSelected &&
      !!this.deliveries?.some(
        (delivery) =>
          delivery.slotDeliveryDetails &&
          !delivery.slotDeliveryDetails.isAutomatic,
      )
    );
  }

  /**
   * Pickup windows that can actually be booked right now. Single source of truth for
   * the picker, for the delivery the store builds and for the checkout button, so the
   * three can never disagree about whether a time can be chosen.
   */
  get bookablePickupSlots(): DeliverySlot[] {
    if (!this.isPickupSelected || !cartStore.pickupSlotsPolicy.enabled) {
      return [];
    }

    return this._bookablePickupSlotsFrom(this.sortedDeliverySlotsList);
  }

  /**
   * The checkout shows the pickup time selector (express toggle / slot rows) when the
   * company has pickup slots enabled and at least one window is bookable.
   */
  get isPickupTimeSelectionAvailable(): boolean {
    return this.bookablePickupSlots.length > 0;
  }

  /**
   * Neither check is belt-and-braces: `deliverySlots` falls back to the courier
   * zone slots when a self-collection cart has no pickup windows of its own, and
   * `company.hasPickupSlots` is the only thing stopping another tenant's `PICKUP`
   * mechanic windows from being treated as bookable pickup times if the backend
   * ever sends `deliverySlotsArePickup` for them. Every pickup slot read goes
   * through here so neither guard can be bypassed.
   */
  private _bookablePickupSlotsFrom(
    slots: DeliverySlot[] | null,
  ): DeliverySlot[] {
    if (
      !company.hasPickupSlots ||
      !orderStore.etaCalculation?.deliverySlotsArePickup
    ) {
      return [];
    }

    const now = millisecondsToSeconds(Date.now());

    return filterBookablePickupSlots(
      slots,
      now,
      backorderFloorFor(
        cartStore.selectedCartItems.map(
          ({ backorderLeadTime }) => backorderLeadTime,
        ),
        now,
      ),
    );
  }

  get hasBackorderItems(): boolean {
    return cartStore.selectedCartItems.some(
      ({ count, sellable, isBackorderAvailable }) =>
        isBackorderAvailable && count > sellable,
    );
  }

  get currentSelectedMethod(): Payment | undefined {
    return orderStore.paymentCards.find(
      (item) => item.id === this.paymentsMethodId,
    );
  }

  get isReadyForPayment() {
    return this._isCardAdded && this._isCardValid;
  }

  setIsCardValid(flag: boolean) {
    this._isCardValid = flag;
  }
  setIsCardAdded(flag: boolean) {
    this._isCardAdded = flag;
  }

  calculateDeliveries(slots: DeliverySlot[] | null) {
    if (this.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT) {
      this.isMultipleDeliveriesAvailable = false;
      this.deliveries = [this._buildPickupDelivery()];
      return;
    }

    // max count of slots
    const normalizedCartItems: NormalizedDeliveryItems[] =
      this._normalizeCartItemsForDeliveries(slots, cartStore.selectedCartItems);
    this.slotSelectionModalStore.resetSelectedSlotForSplit();
    const groupedNotJoinedDeliveries =
      groupByDeliverySlots(normalizedCartItems);

    this.isMultipleDeliveriesAvailable = groupedNotJoinedDeliveries.length > 1;

    this.deliveries =
      this.isDeliverTogether && this.isMultipleDeliveriesAvailable
        ? this._getDeliveryItemWithAllCartItems(slots)
        : groupedNotJoinedDeliveries;
    if (company.isExpressDeliveryOffByDefault) {
      this.slotSelectionModalStore.setIsAutomatic(true);
    }
  }

  /**
   * Self-collection is always one delivery with the whole cart. It carries a pickup
   * window when the company offers them (`cartStore.pickupSlotsPolicy`): the
   * customer's choice — a picked window, or slot mode entered with the express toggle
   * (an `isAutomatic` slot) — survives a rebuild, and ASAP pickup stays the default
   * while slots are optional and the warehouse hands orders out right now.
   *
   * When slots are required there is no ASAP alternative to protect, so the closest
   * bookable window is pre-selected as a real choice — for a first pick, with no
   * previous window to compare against — and the order can be placed with it right
   * away. A previous pick that is no longer bookable and had to be replaced is not a
   * first pick: it is marked `isAutomatic` so the checkout button asks the customer
   * to reconfirm, the same as the optional-slots flow does when its automatic window
   * expires.
   */
  private _buildPickupDelivery(): Delivery {
    const itemsIds = cartStore.selectedCartItems.map((item) => item.sku);
    const { enabled, required } = cartStore.pickupSlotsPolicy;
    const available = this.bookablePickupSlots;

    const asap = (): Delivery => ({
      id: uuid(),
      itemsIds,
      method: DeliveryMethods.CLICK_AND_COLLECT,
    });

    if (!enabled || !available.length) {
      return asap();
    }

    const withSlot = (slot: DeliverySlot, isAutomatic: boolean): Delivery => ({
      id: uuid(),
      itemsIds,
      method: DeliveryMethods.CLICK_AND_COLLECT,
      slotDeliveryDetails: pickupSlotSelection(slot, isAutomatic),
      slot,
    });

    const previous = this.deliveries?.find(
      (delivery) =>
        delivery.method === DeliveryMethods.CLICK_AND_COLLECT &&
        delivery.slotDeliveryDetails,
    )?.slotDeliveryDetails;

    const kept =
      previous &&
      available.find(
        ({ schedule_slot_id, current_date }) =>
          schedule_slot_id === previous.scheduleSlotId &&
          current_date === previous.currentDate,
      );

    if (kept) {
      return withSlot(kept, !required && Boolean(previous.isAutomatic));
    }

    if (required) {
      return withSlot(available[0]!, Boolean(previous));
    }

    // An automatic slot is the slot mode the customer switched into with the express
    // toggle; when it expires the next window takes its place instead of the delivery
    // snapping back to ASAP, which would flip the toggle under their hands.
    if (!previous?.isAutomatic && orderStore.isExpressAvailableNow) {
      return asap();
    }

    return withSlot(available[0]!, true);
  }

  private _normalizeCartItemsForDeliveries(
    slots: DeliverySlot[] | null,
    cartItems?: CartItem[],
  ): NormalizedDeliveryItems[] {
    if (!cartItems) {
      return [];
    }

    const now = millisecondsToSeconds(Date.now());

    return cartItems.map((cartItem) => {
      const { id, sku, backorderLeadTime = 0 } = cartItem;
      const isInStock = isCartItemInWarehouseStock(
        cartItem,
        orderStore.etaCalculation?.warehouse.code,
      );

      const backorderTime = now + hoursToSeconds(backorderLeadTime);
      const closestSlot = (slots || []).find(
        ({ delivery_min_time }) => delivery_min_time >= backorderTime,
      );

      if (isInStock && orderStore.isExpressAvailableNow) {
        if (company.isExpressDeliveryOffByDefault) {
          const selectedSlot = (slots || []).find(
            ({ schedule_slot_id }) =>
              schedule_slot_id ===
              this.slotSelectionModalStore.selectedSlotIdForSplit,
          );

          if (selectedSlot) {
            return {
              itemId: id,
              sku: sku || '',
              slotDeliveryDetails: {
                scheduleSlotId: selectedSlot.schedule_slot_id,
                currentDate: selectedSlot.current_date,
              },
              slot: closestSlot,
            };
          }

          // Pre-select a slot the customer can actually pick. `slots` still
          // holds the ones the picker hides — already past, or earlier than
          // the cart's backorder lead time — so taking the first of those left
          // the details pointing at a slot absent from the picker while `slot`,
          // which is what calculate prices and the picker compares against,
          // pointed at a different one.
          //
          // With no reachable slot at all the delivery is left without slot
          // details, which reads downstream as express — correct here, since
          // this branch has already established express is available and the
          // stock is there.
          if (closestSlot) {
            return {
              itemId: id,
              sku: sku || '',
              slotDeliveryDetails: {
                isAutomatic: true,
                scheduleSlotId: closestSlot.schedule_slot_id,
                currentDate: closestSlot.current_date,
              },
              slot: closestSlot,
            };
          }
        }

        return { itemId: id, sku: sku || '' };
      }

      // FIXME: handle the case where there is no slot available
      if (!closestSlot) {
        return { itemId: id, sku: sku || '' };
      }

      if (
        company.isExpressDeliveryOffByDefault &&
        this.slotSelectionModalStore.selectedSlotIdForSplit
      ) {
        const selectedSlot = (slots || []).find(
          ({ schedule_slot_id }) =>
            schedule_slot_id ===
            this.slotSelectionModalStore.selectedSlotIdForSplit,
        );

        if (selectedSlot && selectedSlot.delivery_min_time >= backorderTime) {
          return {
            itemId: id,
            sku: sku || '',
            slotDeliveryDetails: {
              scheduleSlotId: selectedSlot.schedule_slot_id,
              currentDate: selectedSlot.current_date,
            },
            slot: selectedSlot,
          };
        }
      }

      return {
        itemId: id,
        sku: sku || '',
        slotDeliveryDetails: {
          isAutomatic: company.isExpressDeliveryOffByDefault,
          scheduleSlotId: closestSlot.schedule_slot_id,
          currentDate: closestSlot.current_date,
        },
        slot: closestSlot,
      };
    });
  }

  private _getDeliveryItemWithAllCartItems(
    slots: Nullable<DeliverySlot[]>,
  ): Delivery[] {
    // All the slot dates use seconds for timestamps
    const now = millisecondsToSeconds(Date.now());

    const lastBackorder = cartStore.selectedCartItems.reduce(
      (acc, { backorderLeadTime = 0 }) => {
        const backorderTime = now + hoursToSeconds(backorderLeadTime);

        return Math.max(acc, backorderTime);
      },
      0,
    );

    const closestBackorderSlot = (slots ?? []).find(
      ({ delivery_min_time }) => delivery_min_time >= lastBackorder,
    );

    if (!closestBackorderSlot && company.isExpressDeliveryOffByDefault) {
      return [];
    }

    return groupByDeliverySlots(
      cartStore.selectedCartItems.map(({ sku }) => {
        return {
          sku: sku || '',
          slotDeliveryDetails: {
            isAutomatic: this.slotSelectionModalStore.isAutomatic,
            scheduleSlotId: closestBackorderSlot!.schedule_slot_id,
            currentDate: closestBackorderSlot!.current_date,
          },
          slot: closestBackorderSlot,
        };
      }),
    );
  }

  checkSlotExpirationOfExistingDeliveries(
    slots: DeliverySlot[] | null,
  ): boolean {
    if (!this.deliveries) {
      this.calculateDeliveries(slots);
      return false;
    }

    if (this.isPickupSelected && cartStore.pickupSlotsPolicy.enabled) {
      // One delivery, no splitting: rebuilding is cheaper than reconciling, and
      // `_buildPickupDelivery` keeps a still-valid customer choice. Companies without
      // pickup slots fall through to the reconcile loop below, which is what drops a
      // self-collection leg while the pickup point is closed.
      const before = this.deliveries[0]?.slotDeliveryDetails;

      this.calculateDeliveries(slots);

      return !isSameSlotSelection(
        this.deliveries?.[0]?.slotDeliveryDetails,
        before,
      );
    }

    const dropDeliveries: Delivery[] = [];

    for (const delivery of this.deliveries) {
      if (!delivery.slotDeliveryDetails) {
        if (!orderStore.isExpressAvailableNow) {
          dropDeliveries.push(delivery);
        }
        continue;
      }

      const existingSlot = (slots || []).find(
        (slot) =>
          slot.schedule_slot_id ===
            delivery.slotDeliveryDetails?.scheduleSlotId &&
          slot.current_date === delivery.slotDeliveryDetails?.currentDate,
      );

      if (!existingSlot) {
        dropDeliveries.push(delivery);
      }
    }

    if (!dropDeliveries.length) {
      return false;
    }

    const expectingCartItems = cartStore.selectedCartItems.filter((item) =>
      dropDeliveries.some((d) => d.itemsIds.includes(item.sku)),
    );

    const normalizedCartItems: NormalizedDeliveryItems[] =
      this._normalizeCartItemsForDeliveries(slots, expectingCartItems);

    const existingDeliveriesNormalizedItems: NormalizedDeliveryItems[] =
      this.deliveries
        .filter((delivery) => !dropDeliveries.includes(delivery))
        .map((delivery) =>
          delivery.itemsIds.map((sku) => ({
            itemId: sku,
            sku: sku || '',
            slotDeliveryDetails: delivery.slotDeliveryDetails,
            slot: delivery.slot,
          })),
        )
        .flat();

    const newDeliveries = groupByDeliverySlots([
      ...normalizedCartItems,
      ...existingDeliveriesNormalizedItems,
    ]);

    this.isMultipleDeliveriesAvailable = newDeliveries.length > 1;
    this.deliveries =
      this.isDeliverTogether && this.isMultipleDeliveriesAvailable
        ? this._getDeliveryItemWithAllCartItems(slots)
        : newDeliveries;
    if (company.isExpressDeliveryOffByDefault) {
      this.slotSelectionModalStore.setIsAutomatic(true);
    }
    return true;
  }

  // Setters
  setDeliveries(deliveries: Delivery[] | null) {
    this.deliveries = deliveries;
  }

  setHasExactAmount(value: boolean) {
    this.changeFromAmount = null;
    this.hasExactAmount = value;
  }

  setChangeFromAmount(value: Nullable<number>) {
    this.hasExactAmount = false;
    this.changeFromAmount = value;
  }

  setMobileMoneyPhoneVal(value: string) {
    this.mobileMoneyPhoneVal = value;
  }

  setAddressVal(val: string) {
    this.addressVal = val;
  }

  setAddress2Val(val: string) {
    this.address2Val = val;
  }

  setAddressFloorVal(val: string) {
    this.addressFloorVal = val;
  }

  setAddressBldVal(val: string) {
    this.addressBldVal = val;
  }

  setAddressAptVal(val: string) {
    this.addressAptVal = val;
  }

  setPhoneVal(val: string) {
    this.phoneVal = val;
  }

  setIsPhoneValid(flag: boolean) {
    this.isPhoneValid = flag;
  }

  setIsEmailSync(flag: boolean) {
    if (flag) {
      this.setEmailVal(this.userStore.personalData.email || '');
    }
    this.isEmailSync = flag;
  }

  setIsPersonalDataSync(flag: boolean) {
    this.isPersonalDataSync = flag;
  }

  setNameVal(val: string) {
    this.nameVal = val;
  }

  setEmailVal(val: string) {
    this.emailVal = val;
  }

  setExternalRecipientValid(value: boolean) {
    this.isExternalRecipientValid = value;
  }

  setCommentVal(val: string) {
    this.commentVal = val;
  }

  setInstructionList(val: Record<number, string>) {
    this.instructionList = val;
  }

  setRecipientTempPhoneVal(val: string) {
    this.recipientTempPhoneVal = val;
  }

  setIsRecipientTempPhoneValid(flag: boolean) {
    this.isRecipientTempPhoneValid = flag;
  }

  setRecipientTempNameVal(val: string) {
    this.recipientTempNameVal = val;
  }

  setRecipientTempEmailVal(val: string) {
    this.recipientTempEmailVal = val;
  }

  setAgreeSaveCard(flag: boolean) {
    this.agreeSaveCard = flag;
  }

  setIsLoading(flag: boolean) {
    this.isLoading = flag;
  }

  setPaymentIntent(newOrderResponse: NewOrderResponse | null) {
    if (!newOrderResponse) {
      this.paymentIntentMultiorder = null;
      this.paymentIntent = null;
      this.createdOrdersCount = 0;
      return;
    }

    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    this.paymentIntentMultiorder = newOrderResponse.data.multi_order;
    this.paymentIntent = order;
    this.createdOrdersCount = newOrderResponse.data.orders.length;
  }

  setCancellationDelay(delay: number | null) {
    this.cancellationDelay = delay;
  }

  setIsThreeds2Active(isActive: boolean) {
    this.isThreeds2Active = isActive;
  }

  setPaymentMethod(val: { card: StripeCardElement } | string) {
    this.paymentMethod = val;
  }

  setOrderId(val: string) {
    this.orderId = val;
  }

  setError(val: string | null) {
    if (val) {
      mainStore.pushAlert('error', val);
    }

    this.error = val;
  }

  setIsDisabled(flag: boolean) {
    this.isDisabled = flag;
  }

  setIsPersonalDataChanged(flag: boolean) {
    this.isPersonalDataChanged = flag;
  }

  setIsShowRecipientPopover(flag: boolean) {
    this.isShowRecipientPopover = flag;
  }

  setIsUnableConfirmCardPayment(flag: boolean) {
    this.isUnableConfirmCardPayment = flag;
  }

  setRecipient() {
    this.recipientPhoneVal = toJS(this.recipientTempPhoneVal);
    this.isRecipientPhoneValid = toJS(this.isRecipientTempPhoneValid);
    this.recipientNameVal = toJS(this.recipientTempNameVal);
    this.recipientEmailVal = toJS(this.recipientTempEmailVal);
  }

  addInstruction(val: string, index: number) {
    this.instructionList[index] = val;
  }

  deleteInstruction(index: number) {
    delete this.instructionList[index];
  }

  resetInstructionList() {
    this.instructionList = {};
  }

  setDeliverEarlier(value: boolean) {
    this.deliverEarlier = value;
  }

  setActivePaymentMethod(
    paymentMethod: PaymentMethod | null,
    options?: { skipAnalytics?: boolean },
  ) {
    this.activePaymentMethod = paymentMethod;

    if (
      Object.values(ETAPaymentMethod).includes(
        paymentMethod as ETAPaymentMethod,
      )
    ) {
      this.activePaymentSystem =
        orderStore.paymentMethodsMap.get(
          paymentMethod as ETAPaymentMethod,
        )?.[0] || null;
    } else if (
      paymentMethod === MobilePaymentMethod.ApplePay ||
      paymentMethod === MobilePaymentMethod.GooglePay
    ) {
      const cardSystems =
        orderStore.paymentMethodsMap.get(ETAPaymentMethod.Card) ?? [];
      const walletSystems: PaymentSystem[] = [
        PaymentSystem.stripe,
        PaymentSystem.adyen,
        PaymentSystem.verifone,
        PaymentSystem.myfatoorah,
      ];
      this.activePaymentSystem =
        cardSystems.find((sys) => walletSystems.includes(sys)) ??
        cardSystems[0] ??
        null;
    } else {
      this.activePaymentSystem = null;
    }

    this.setError(null);
    eventEmitter.emit(EventsName.PAYMENT_METHOD_CHANGED);

    if (!options?.skipAnalytics) {
      analyticsService.track(AnalyticsEvent.PAYMENT_METHOD_SELECTED, {
        payment_method: paymentMethod,
        payment_system: this.activePaymentSystem,
      });
    }
  }

  setIsAddNewCard(flag: boolean) {
    this.isAddNewCard = flag;
    this.setIsDisabled(flag);
    this.setError(null);
  }

  setPayments(val: { value: string; text: string }[] | null) {
    this.payments = val;
  }

  setArePaymentsLoaded(flag: boolean) {
    this.arePaymentsLoaded = flag;
  }

  setPaymentsMethodId(val: string | null) {
    this.paymentsMethodId = val;
  }

  setIsShowTimeoutPopover(flag: boolean) {
    this.isShowTimeoutPopover = flag;
  }

  setIsFarFromDeliveryAddress(flag: boolean) {
    this.isFarFromDeliveryAddress = flag;
  }

  /**
   * Whether the passive geofencing notice should be on screen. Read from both
   * checkout layouts, which place the banner differently, so the condition
   * lives here rather than being duplicated at each call site.
   *
   * `isGeofencingEnabled` is re-checked rather than assumed: it is what gates
   * the distance check that sets the flag, so a tenant given the presentation
   * flag alone would otherwise render a banner nothing keeps up to date.
   */
  get isGeofencingNoticeShown() {
    return (
      company.isGeofencingEnabled &&
      company.showGeofencingNotice &&
      this.isFarFromDeliveryAddress
    );
  }

  setDeliveryMethod(method: DeliveryMethods) {
    if (this.deliveryMethod !== method) {
      this.deliveryMethodChangeMeta.setLoading(true);
    }
    // this assign will trigger reaction that runs calculate deliveries
    this.deliveryMethod = method;
  }

  setPendingDeliveryMethod(method: DeliveryMethods | null) {
    this.pendingDeliveryMethod = method;
  }

  setIsUpdatedDeliveryPopover(flag: boolean) {
    this.isUpdatedDeliveryPopover = flag;
  }

  setDeliveryPrices(val: RequestETAResponse) {
    this.deliveryPrices = val;
  }

  setUseBonuses(flag: boolean) {
    this.useBonuses = flag;
  }

  setSessionId(val: string) {
    this.sessionId = val;
  }

  setDeliverySlots(slots: DeliverySlotsResponse) {
    this.deliverySlots = slots;
  }
  setDeliveryAllTogether(val: boolean): void {
    this.deliveryAllTogether = val;
  }

  setSelectedDate(val: string) {
    this.selectedDeliveryDate = val;
    if (this.deliverySlots && val !== '0') {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      this.setSelectedSlot(this.deliverySlots[val][0]);
    } else {
      this.setSelectedSlot(null);
    }
  }

  setSeletectedDateExpress() {
    this.selectedDeliveryDate = '0';
    this.selectedDeliverySlot = null;
  }

  // TODO: is it obsolete now?
  async setSelectedSlot(val: DeliverySlot | null) {
    this.selectedDeliverySlot = val;

    await orderStore.requestETA();
    await cartStore.calculateCart();
  }

  setIsNewAdyenCard(flag: boolean) {
    this._isNewAdyenCard = flag;
  }

  setIsAdyenCardValid(flag: boolean) {
    this._isAdyenCardValid = flag;
  }

  calculateFullDeliveryFee() {
    let fullDeliveryPrice = 0;

    this.deliveries?.forEach((_, index) => {
      const order = cartStore.deliveriesOrders[index];
      if (!order) {
        return;
      }
      if (order.is_delivery_free) {
        return;
      }

      const deliveryPrice = formatPrice(
        mainStore.convertPenceToPounds(order.delivery_info.paid_price),
      );

      fullDeliveryPrice = +fullDeliveryPrice + +deliveryPrice;
    });

    return `${fullDeliveryPrice}`;
  }

  // Actions
  getOrderData(additionalData?: AdditionalPaymentData): OrderData {
    let instructions = Object.values(this.instructionList);

    let customerCash = 0;
    const hasExactAmount = this.hasExactAmount;
    if (hasExactAmount) {
      instructions.push(this.getExactAmountInstruction());
    } else if (isNumber(this.changeFromAmount)) {
      customerCash = Math.abs(Number(this.changeFromAmount.toFixed(2)));
    }

    const notes = [
      company.hasNoteAddress2 ? this.address2Val : '',
      instructions.join(', ').toUpperCase(),
      this.isDeliverEarlierRequested ? DELIVER_EARLIER_NOTE : '',
      this.commentVal,
    ]
      .filter((i) => i)
      .join(', ');
    if (this.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT) {
      instructions = [];
    }
    let isSaveCard = toJS(this.agreeSaveCard);
    if (
      mainStore.isRN &&
      this.activePaymentMethod === MobilePaymentMethod.ApplePay &&
      mainStore.isApplePay
    ) {
      isSaveCard = false;
    }
    if (
      mainStore.isRN &&
      this.activePaymentMethod === MobilePaymentMethod.GooglePay &&
      mainStore.isGooglePay
    ) {
      isSaveCard = false;
    }

    return {
      address: this.addressVal,
      address2: this.address2Val,
      addressFloor: this.addressFloorVal,
      addressBld: this.addressBldVal,
      addressApt: this.addressAptVal,
      fullName: this.isRecipientValid
        ? this.recipientNameVal
        : company.isDubaiExpressCompany
          ? this.nameVal.trim() || this.userStore.fullName
          : this.userStore.fullName,
      email: this.isRecipientValid
        ? this.recipientEmailVal.trim()
        : this.emailVal.trim(),
      phone: this.isRecipientValid ? this.recipientPhoneVal : this.phoneVal,
      mobileMoneyPhone: this.mobileMoneyPhoneVal.trim(),
      comment: this.commentVal,
      instructions,
      notes,
      saveCard: isSaveCard,
      deliveryMethod: this.deliveryMethod,
      sessionId: this.sessionId,
      customerCash,
      hasExactAmount,
      additionalData,
      ...(this.selectedDeliverySlot && this.selectedDeliveryDate
        ? {
            slot_delivery_details: {
              schedule_slot_id: this.selectedDeliverySlot.schedule_slot_id,
              current_date: +this.selectedDeliveryDate / 1000,
            },
          }
        : {}),
    };
  }

  switchDeliverTogether() {
    this.isDeliverTogether = !this.isDeliverTogether;
    mainStore.sendToRN('logAmplitudeEvent', {
      name: 'Delivery option changed',
      params: { type: this.isDeliverTogether ? 'single' : 'split' },
    });
    cartStore.calculateCart();
  }

  autofillRecipient() {
    this.recipientTempPhoneVal = toJS(this.phoneVal);
    this.isRecipientTempPhoneValid = toJS(this.isPhoneValid);
    this.recipientTempNameVal = this.userStore.fullName;
    this.recipientTempEmailVal = toJS(this.emailVal);
  }

  /**
   * Builds the snapshot every purchase event is rendered from.
   *
   * Taken while the cart and the ETA calculation are still in memory: by the
   * time the PSP returns, `onPaymentSuccess` has emptied the cart, and after a
   * WebView reload or a cold start nothing of it was ever loaded.
   *
   * @param orderId - id of the created order
   * @param wasFirstOrder - whether this was the customer's first order, read
   * from `orderStore.isFirstOrder` before the success flow resets it. `null`
   * means the flag was never resolved and the persisted order counter is used
   * as a fallback — that counter starts at zero in a fresh store and is only
   * filled by a network call, so it alone cannot gate first-purchase events.
   */
  buildPurchaseSnapshot(
    orderId: string,
    wasFirstOrder: Nullable<boolean>,
  ): PurchaseSnapshot {
    return {
      orderId,
      createdAt: Date.now(),
      isFirstPurchase:
        wasFirstOrder ?? mainStore.analytics.totalSuccessfulOrders === 0,
      currency: orderStore.currency,
      finalPrice: cartStore.finalPrice,
      basePrice: cartStore.totalCartPrice.base,
      itemsAmount: cartStore.totalCartCount,
      productsAmount: cartStore.cart.reduce((sum, item) => sum + item.count, 0),
      etaMin: orderStore.etaCalculation?.duration.min || 0,
      etaMax: orderStore.etaCalculation?.duration.max || 0,
      deliveryFee: orderStore.fee.shippingPounds || 0,
      threshold: orderStore.fee.thresholdPounds || 0,
      isSurger:
        orderStore.etaSlotCalculation?.highDemand ||
        orderStore.etaCalculation?.highDemand ||
        false,
      orderNum: mainStore.analytics.totalSuccessfulOrders,
      promocode: cartStore.promocode.value,
      warehouseCode:
        orderStore.etaSlotCalculation?.warehouse.code ||
        orderStore.etaCalculation?.warehouse.code ||
        '',
      items: cartStore.cartForFirebase,
      deliveryInstructions: Object.values(this.instructionList).filter(
        (instruction): instruction is string => typeof instruction === 'string',
      ),
    };
  }

  purchaseCompletedAnalytics(
    orderId: string,
    wasFirstOrder: Nullable<boolean>,
  ) {
    const snapshot =
      purchaseRecovery.getPending(orderId) ??
      this.buildPurchaseSnapshot(orderId, wasFirstOrder);

    if (!analyticsService.isConsentGranted()) {
      purchaseRecovery.remember(snapshot);
      return;
    }

    if (!purchaseRecovery.markSentIfNotYet(orderId)) {
      return;
    }

    trackPurchase(snapshot);
  }

  initStateData() {
    if (!this.stateData || Date.now() - this.stateData.timestamp > 6000) {
      this.stateData = null;
      this.isRefreshed = false;
      return;
    }
    const stateData = toJS(this.stateData);
    this.addressVal = stateData.addressVal;
    this.address2Val = stateData.address2Val;
    this.addressFloorVal = stateData.addressFloorVal;
    this.addressBldVal = stateData.addressBldVal;
    this.addressAptVal = stateData.addressAptVal;
    this.commentVal = stateData.commentVal;
    this.instructionList = stateData.instructionList;
    this.recipientPhoneVal = stateData.recipientPhoneVal;
    this.isRecipientPhoneValid = true;
    this.recipientNameVal = stateData.recipientNameVal;
    this.recipientEmailVal = stateData.recipientEmailVal;
    this.isAddNewCard = stateData.isAddNewCard;
    this.agreeSaveCard = stateData.agreeSaveCard;
    if (stateData.isAddNewCard) {
      this.isDisabled = true;
    }
    this.isRefreshed = true;
    this.stateData = null;
    this.deliveryMethod = stateData.deliveryMethod;
  }

  initActivePaymentMethod() {
    const silent = { skipAnalytics: true } as const;

    if (!this.availablePaymentMethods.length) {
      this.setActivePaymentMethod(null, silent);
      return;
    }

    if (
      this.activePaymentMethod &&
      this.availablePaymentMethods.includes(this.activePaymentMethod)
    ) {
      this.setActivePaymentMethod(this.activePaymentMethod, silent);
      return;
    }

    const firstPhoneMethod = this.availablePaymentMethods.find((m) =>
      isPhonePaymentMethod(m as ETAPaymentMethod),
    );
    if (firstPhoneMethod) {
      this.setActivePaymentMethod(firstPhoneMethod, silent);
      return;
    }
    if (this.availablePaymentMethods.includes(ETAPaymentMethod.QR)) {
      this.setActivePaymentMethod(ETAPaymentMethod.QR, silent);
      return;
    } else if (this.availablePaymentMethods.includes(ETAPaymentMethod.Card)) {
      this.setActivePaymentMethod(ETAPaymentMethod.Card, silent);
      return;
    } else if (this.availablePaymentMethods.includes(ETAPaymentMethod.Cash)) {
      this.setActivePaymentMethod(ETAPaymentMethod.Cash, silent);
      return;
    }
    const [first] = this.availablePaymentMethods;
    if (!first) {
      this.setActivePaymentMethod(null, silent);
      return;
    }

    this.setActivePaymentMethod(first, silent);
  }

  async requestPayments() {
    if (
      this.activePaymentSystem === PaymentSystem.myfatoorah ||
      (this.activePaymentSystem &&
        isSettledOfflineSystem(this.activePaymentSystem)) ||
      (this.activePaymentSystem === PaymentSystem.octo &&
        !company.isOctoSavedCardsEnabled)
    ) {
      this.setPayments(null);
      this.setPaymentsMethodId(null);
      this.setArePaymentsLoaded(true);
      return;
    }

    try {
      const deliveryZoneId =
        orderStore.etaCalculation?.warehouse.zone[this.deliveryMethod]?.id;
      await orderStore
        .requestPayments(deliveryZoneId)
        .catch((error) => error && console.error(error));
      if (!orderStore.paymentCards?.length) {
        this.setPayments(null);
        this.setPaymentsMethodId(null);
        return;
      }

      const paymentList: Array<{ value: string; text: string }> =
        orderStore.paymentCards?.map((card) => {
          const {
            id,
            card: { brand, last4, exp_month, exp_year },
          } = card;

          const brandName = brand.toLowerCase().replace(/\s/g, '');

          const cardIcon = CARD_BRANDS[brandName]?.icon ?? UnknownCard;
          const cardTitle = CARD_BRANDS[brandName]?.title ?? brand;
          const expDate =
            exp_year &&
            exp_month &&
            `${mainStore.addLeadingZero(exp_month)}/${exp_year
              .toString()
              .slice(-2)}`;

          return {
            value: id,
            text: `
             ${cardIcon ? `<img class="" src="${cardIcon}" alt="" />` : ''}
              <div class="card-main text-truncate text-capitalize">
                <p class="card-main__brand">${cardTitle}</p>
                <div class="card-main__number">${last4}</div>
              </div>
              ${expDate ? ` <div class="card-date">${expDate}</div>` : ''}
             `,
          };
        });

      const defaultIndex = orderStore.paymentCards.findIndex(
        (el) => el.metadata.is_default === 'true',
      );
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      paymentList.splice(0, 0, paymentList.splice(defaultIndex || 0, 1)[0]);
      this.setPayments(paymentList);
      if (paymentList.length) {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        this.setPaymentsMethodId(paymentList[0].value);
        if (!this.isAddNewCard) {
          this.setIsDisabled(false);
        }
        analyticsService.track(
          AnalyticsEvent.PURCHASE_PAYMENT_METHOD_FILLED_IN,
          {
            cart_id: undefined,
            payment_method: 'card',
            type: 'existing',
          },
        );
        analyticsService.track(AnalyticsEvent.ADD_PAYMENT_INFO, {
          currency: orderStore.currency.toUpperCase(),
          payment_type: 'credit card',
          value: mainStore.toFloat(cartStore.finalPrice),
          items: cartStore.cartForFirebase,
          coupon: cartStore.promocode.value,
        });
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        !error.response &&
        (error.code === 'ECONNABORTED' || error.message === 'Network Error')
      ) {
        return;
      }
      reportWarning(DOMAIN.payment, 'Failed payments loading', {
        reason: REASON.payment.methodsLoadFailed,
        extras: {
          error: (error as AxiosError).response,
        },
      });
    } finally {
      this.setArePaymentsLoaded(true);
    }
  }

  /**
   * Persists the DubaiExpress recipient email to the customer record before the
   * order is placed. `/orders/new` validates the recipient against the customer
   * and does not update it, so a stale customer email makes the order fail. The
   * inline RecipientFields form syncs on blur, but this awaited guard closes the
   * race where the order is submitted before that sync completes. Returns false
   * when the email could not be saved (e.g. already in use) so the caller aborts
   * instead of submitting a doomed order.
   */
  private async ensureRecipientSynced(): Promise<boolean> {
    if (!company.isDubaiExpressCompany) {
      return true;
    }
    const email = this.emailVal.trim();
    if (!email || email === (this.userStore.personalData.email ?? '')) {
      return true;
    }
    if (!mainStore.validateEmail(email)) {
      return false;
    }
    return await this.userStore.updatePersonalEmail(email);
  }

  async orderCheckout(
    stripe?: Stripe | null,
    elements?: StripeElements | null,
    additionalData?: AdditionalPaymentData,
  ) {
    try {
      checkoutAttempt.start(
        () => this.isLoading || this.isAwaitingProviderPayment,
        {
          paymentSystem: this.activePaymentSystem ?? null,
          paymentMethod: this.activePaymentMethod ?? null,
          deliveryMethod: this.deliveryMethod ?? null,
        },
      );
      // Loading must be set before the recipient-sync network call so the
      // pay button is disabled immediately, preventing a double submit.
      this.setIsLoading(true);
      if (!(await this.ensureRecipientSynced())) {
        this.setIsLoading(false);
        return;
      }
      trace(DOMAIN.checkout, 'Starting checkout process', {
        stage: 'checkout_start',
        checkoutDetails: {
          finalPrice: cartStore.finalPrice,
          pencePrice: mainStore.convertPoundsToPence(cartStore.finalPrice),
          cartItemsCount: cartStore.totalCartCount,
          cartTotalPrice: cartStore.totalCartPrice,
          hasBackorderItems: this.hasBackorderItems,
        },
        paymentDetails: {
          activePaymentMethod: this.activePaymentMethod,
          activePaymentSystem: this.activePaymentSystem,
          availablePaymentMethods: this.availablePaymentMethods,
          paymentSystem: this.activePaymentSystem,
          isStripeAvailable: !!stripe && !!elements,
          isApplePayAvailable: mainStore.isApplePay,
          isGooglePayAvailable: mainStore.isGooglePay,
        },
        deliveryDetails: {
          method: this.deliveryMethod,
          // Which fields are filled, not what is in them. Every question this
          // has ever been used to answer — is the building missing, did the
          // form lose the flat number — is answered by the shape, and the
          // street a customer lives on is not ours to keep in an issue.
          // `etaCalculation` still carries the coordinates, which is what
          // geo failures actually need.
          address: {
            hasAddressLine: Boolean(this.addressVal),
            hasAddressLine2: Boolean(this.address2Val),
            hasFloor: Boolean(this.addressFloorVal),
            hasBuilding: Boolean(this.addressBldVal),
            hasApartment: Boolean(this.addressAptVal),
          },
          etaCalculation: orderStore.etaCalculation,
        },
        validationState: {
          isPersonalDataValid: this.isPersonalDataValid,
          isFormValid: this.isFormValid,
          isPhoneValid: this.isPhoneValid,
          isEmailValid: this.isEmailValid,
        },
        promocode: cartStore.promocode,
        timestamp: new Date().toISOString(),
      });

      if (mainStore.convertPoundsToPence(cartStore.finalPrice) < 50) {
        reportWarning(DOMAIN.checkout, 'Price below minimum threshold', {
          reason: REASON.checkout.priceBelowMinimum,
          extras: {
            stage: 'checkout_validation_failed',
            error: 'price_too_low',
            price: cartStore.finalPrice,
            pencePrice: mainStore.convertPoundsToPence(cartStore.finalPrice),
          },
        });
        mainStore.setIsZeroCartPopover(true);
        this.setIsLoading(false);
        throw new Error('price < 50');
      }

      this.setPaymentIntent(null);
      this.setIsUnableConfirmCardPayment(false);
      this.setError(null);

      await this.syncAddress();
      checkoutAttempt.step('address_synced');
      let isCartChanged: boolean;
      try {
        isCartChanged = await cartStore.calculateCart({ throwOnError: true });
      } catch {
        // handleCalculateError has already turned the rejection into UI.
        this.setIsLoading(false);
        throw new OrderErrorHandled();
      }
      checkoutAttempt.step('cart_calculated', { isCartChanged });

      if (company.hasNewLogistics && cartStore.v3HasBlockingIssue) {
        reportWarning(DOMAIN.checkout, 'V3 calculate blocking issue', {
          reason: REASON.checkout.processFailed,
          extras: {
            blockingReason: cartStore.v3BlockingIssueReason,
          },
        });
        this.setIsLoading(false);
        throw new Error('V3 calculate has blocking issue');
      }

      if (isCartChanged) {
        reportWarning(DOMAIN.checkout, 'Cart changed during checkout', {
          reason: REASON.checkout.cartChanged,
          extras: {
            stage: 'cart_validation_failed',
            error: 'cart_changed',
            cartChanges: {
              oldCart: cartStore.cart.map((item) => ({
                sku: item.sku,
                name: item.name,
                count: item.count,
                price: item.price,
              })),
              newCart: cartStore.selectedCartItems.map((item) => ({
                sku: item.sku,
                name: item.name,
                count: item.count,
                price: item.price,
              })),
            },
          },
        });
        this.setIsLoading(false);
        throw new Error('cart is changed');
      }

      if (orderStore.gift && orderStore.activeGift) {
        orderStore.setIsAuthInCart(false);
        const isGiftsFetched = await orderStore.fetchGifts();
        if (!isGiftsFetched) {
          this.setIsLoading(false);
          throw new Error("Can't fetch gifts");
        }
      }

      if (this.deliveryMethod !== DeliveryMethods.CLICK_AND_COLLECT) {
        const deliveryPrices = await orderStore.requestETA();
        checkoutAttempt.step('eta_received', {
          hasEta: !!orderStore.etaCalculation,
        });

        trace(DOMAIN.checkout, 'ETA calculation completed', {
          stage: 'eta_calculation',
          etaResponse: {
            cost: orderStore.etaCalculation?.cost,
            duration: orderStore.etaCalculation?.duration,
            warehouse: orderStore.etaCalculation?.warehouse,
            highDemand: orderStore.etaCalculation?.highDemand,
            paymentMethods: orderStore.etaCalculation?.paymentMethods,
          },
          deliveryPrices: {
            oldPrice: deliveryPrices?.oldPrice,
            newPrice: deliveryPrices?.newPrice,
          },
          deliveryMethod: this.deliveryMethod,
          cartDetails: {
            itemCount: cartStore.totalCartCount,
            finalPrice: cartStore.finalPrice,
          },
        });

        if (
          deliveryPrices &&
          deliveryPrices.oldPrice !== deliveryPrices.newPrice
        ) {
          reportWarning(DOMAIN.checkout, 'Delivery price changed after retry', {
            reason: REASON.checkout.deliveryPriceChanged,
            extras: {
              stage: 'delivery_price_changed_after_retry',
              oldPrice: deliveryPrices.oldPrice,
              newPrice: deliveryPrices.newPrice,
            },
          });
          this.setIsLoading(false);
          throw new Error('oldPrice !== newPrice');
        }

        if (!orderStore.etaCalculation) {
          reportFailure(DOMAIN.checkout, 'Missing ETA calculation data', {
            reason: REASON.checkout.missingEta,
            extras: {
              stage: 'eta_calculation_missing',
              error: 'missing_eta_data',
            },
          });
          this.setIsLoading(false);
          throw new Error('!orderStore.etaCalculation');
        }
      }

      const activePaymentMethod = this.activePaymentMethod;
      const isDeliverySlotsChanged = await orderStore.reCalculateDeliveries();
      checkoutAttempt.step('deliveries_recalculated', {
        isDeliverySlotsChanged: !!isDeliverySlotsChanged,
      });

      if (isDeliverySlotsChanged) {
        reportWarning(DOMAIN.checkout, 'Delivery slots changed', {
          reason: REASON.checkout.deliverySlotsChanged,
          extras: {
            stage: 'delivery_slots_changed',
            error: 'slots_changed',
            oldSlots: this.deliverySlots,
          },
        });
        this.setIsLoading(false);
        throw new Error('Delivery slots changed');
      }

      this.initActivePaymentMethod();
      if (activePaymentMethod !== this.activePaymentMethod) {
        reportWarning(DOMAIN.checkout, 'Payment method changed', {
          reason: REASON.checkout.paymentMethodChanged,
          extras: {
            stage: 'payment_method_changed',
            error: 'method_changed',
            oldMethod: activePaymentMethod,
            newMethod: this.activePaymentMethod,
          },
        });
        this.setIsLoading(false);
        throw new Error('Payment method is changed');
      }

      let walletPaymentResult;

      if (this.activePaymentSystem === PaymentSystem.verifone) {
        try {
          if (this.activePaymentMethod === MobilePaymentMethod.ApplePay) {
            const data = await this.processWalletPaymentRequest('applePay');
            walletPaymentResult = data.result;
          } else if (
            this.activePaymentMethod === MobilePaymentMethod.GooglePay
          ) {
            const data = await this.processWalletPaymentRequest('googlePay');
            walletPaymentResult = data.result;
          }
        } catch (error) {
          const err = error as {
            code?: string;
            errorCode?: string;
            error?: { code?: string; errorCode?: string };
          };
          const isCanceled =
            err?.code === 'Canceled' ||
            err?.code === 'Cancelled' ||
            err?.code === 'E_USER_CANCELLED' ||
            err?.errorCode === 'canceledByShopper' ||
            err?.error?.code === 'Canceled' ||
            err?.error?.code === 'Cancelled' ||
            err?.error?.code === 'E_USER_CANCELLED' ||
            err?.error?.errorCode === 'canceledByShopper';

          if (isCanceled) {
            this.setIsLoading(false);
            return;
          }

          reportFailure(DOMAIN.payment, 'Payment Request API failed', {
            reason: REASON.payment.walletRequestApiFailed,
            extras: {
              error,
              paymentMethod: this.activePaymentMethod,
              paymentAmount: cartStore.finalPrice,
            },
          });
          this.setIsLoading(false);
          throw error;
        }
      }

      let response: NewOrderResponse | null = null;
      try {
        trace(DOMAIN.checkout, 'Creating new order', {
          stage: 'creating_order',
          orderData: this.getOrderData(additionalData),
          slots: this.deliverySlots,
        });

        // Prepare checkout data based on payment method
        let checkoutData = additionalData;

        if (walletPaymentResult) {
          const isApplePay =
            this.activePaymentMethod === MobilePaymentMethod.ApplePay;
          const isGooglePay =
            this.activePaymentMethod === MobilePaymentMethod.GooglePay;

          let walletPayload;
          let checkoutMethod;

          if (isApplePay) {
            walletPayload =
              walletPaymentResult.paymentResponse.details.applePayToken
                .paymentData;
            checkoutMethod = 'APPLE_PAY';

            trace(
              DOMAIN.payment,
              'Creating order with Apple Pay wallet payload',
              {
                hasToken: !!walletPayload,
              },
            );
          } else if (isGooglePay) {
            walletPayload =
              walletPaymentResult.paymentResponse.details.androidPayToken
                .paymentData;
            checkoutMethod = 'GOOGLE_PAY';

            trace(
              DOMAIN.payment,
              'Creating order with Google Pay wallet payload',
              {
                hasToken: !!walletPayload,
              },
            );
          }

          checkoutData = {
            checkoutMethod,
            ...additionalData,
            paymentMethodInfo: {
              walletPayload: walletPayload.paymentData,
            },
          };
        }

        response = await orderStore.newOrder(this.getOrderData(checkoutData));
      } catch (error) {
        this.setIsLoading(false);
        throw error;
      }
      if (!response || !response.data.orders.length) {
        this.setIsLoading(false);
        throw new Error('No orders');
      }

      this.setPaymentIntent(response);

      const createdOrderId = response.data.orders[0]?.order?.id ?? '';
      checkoutAttempt.setOrderId(createdOrderId);

      /**
       * The one healthy milestone this project records as an event.
       *
       * Every other report here describes something going wrong, which left no
       * way to tell a quiet evening from a broken checkout, and no way to count
       * how many orders one customer had to create before one of them stuck.
       * On 26 August a single CityDrinks shopper produced four orders in four
       * minutes and Sentry held exactly one event about it, because the other
       * three attempts failed in ways nothing reported on.
       *
       * The payment method is in the fingerprint rather than the extras on
       * purpose: `search_events` is unavailable on this instance, so an issue's
       * event count is the only aggregate there is, and splitting on the method
       * turns that count into "orders created, by how they were paid for".
       */
      reportInfo(DOMAIN.checkout, 'Order created', {
        reason: REASON.checkout.orderCreated,
        fingerprint: [this.activePaymentMethod ?? 'unknown'],
        extras: {
          orderId: response.data.orders[0]?.order?.id ?? null,
          paymentMethod: this.activePaymentMethod,
          paymentSystem: this.activePaymentSystem,
          orderAmount: cartStore.finalPrice,
          currency: orderStore.currency,
        },
      });

      if (createdOrderId) {
        purchaseRecovery.remember(
          this.buildPurchaseSnapshot(createdOrderId, orderStore.isFirstOrder),
        );
      }

      if (
        !isSettledOfflineMethod(this.activePaymentMethod as ETAPaymentMethod)
      ) {
        checkoutAttempt.paymentStarted(
          `${this.activePaymentSystem ?? 'unknown'}:${this.activePaymentMethod ?? 'unknown'}`,
          {
            hasPaymentLink:
              !!response.data.orders[0]?.payment?.providerPaymentDetails
                ?.paymentLink,
            hasPaymentIntent: !!response.data.orders[0]?.payment_intent,
            isRN: mainStore.isRN,
          },
        );
        trace(DOMAIN.payment, 'Initiating payment', {
          stage: 'initiating_payment',
          paymentSystem: this.activePaymentSystem,
          paymentMethod: this.activePaymentMethod,
          orderId: response.data.orders[0]?.order?.id,
        });

        if (createdOrderId) {
          analyticsService.track(AnalyticsEvent.PURCHASE_PAYMENT_STARTED, {
            order_id: createdOrderId,
            price: cartStore.totalCartPrice.base,
            final_price: cartStore.finalPrice,
            items_amount: cartStore.totalCartCount,
            products_amount: cartStore.cart.reduce(
              (sum, item) => sum + item.count,
              0,
            ),
            currency: orderStore.currency.toUpperCase(),
            payment_method: this.activePaymentMethod ?? '',
            payment_system: this.activePaymentSystem ?? '',
          });
        }

        if (this.activePaymentSystem === PaymentSystem.pvit) {
          this.setIsLoading(false);
          this.setOrderId(response.data.orders[0]?.order?.id ?? '');
          return response;
        }

        if (this.activePaymentSystem === PaymentSystem.myfatoorah) {
          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.ApplePay &&
            mainStore.isApplePay
          ) {
            this.applePay(response);
            return;
          }
          if (response.data.orders[0]?.payment.myFatoorahPayment.error) {
            reportFailure(DOMAIN.payment, 'MyFatoorah payment error', {
              reason: REASON.payment.myfatoorahError,
              extras: {
                stage: 'myfatoorah_error',
                error: response.data.orders[0]?.payment.myFatoorahPayment.error,
              },
            });
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            this.setIsLoading(false);
            throw new Error(
              'response.data.orders[0]?.payment.myFatoorahPayment.error',
            );
          }
          if (response.data.orders[0]?.payment?.myFatoorahPayment.paymentURL) {
            trace(DOMAIN.payment, 'Redirecting to MyFatoorah payment', {
              stage: 'myfatoorah_redirect',
              paymentUrl:
                response.data.orders[0]?.payment?.myFatoorahPayment.paymentURL,
            });
            if (mainStore.isRN) {
              mainStore.sendToRN('openInAppBrowser', {
                url: response.data.orders[0]?.payment?.myFatoorahPayment
                  .paymentURL,
              });
            } else {
              window
                .open(
                  response.data.orders[0]?.payment?.myFatoorahPayment
                    .paymentURL,
                  '_blank',
                )
                ?.focus();
            }
          }
        }

        if (this.activePaymentSystem === PaymentSystem.octo) {
          const octoOrderId = response.data.orders[0]?.order?.id ?? '';
          const paymentLink =
            response.data.orders[0]?.payment?.providerPaymentDetails
              ?.paymentLink;

          if (paymentLink) {
            this.recordPaymentLink('octo', paymentLink);
            trace(DOMAIN.payment, 'Opening Octo payment iframe', {
              stage: 'octo_iframe',
              paymentLink,
            });

            this.octoPaymentLink = paymentLink;

            if (mainStore.isRN) {
              this.openPaymentInAppBrowser('octo', paymentLink);
            }
          } else if (octoOrderId && company.isOctoSavedCardsEnabled) {
            // Frictionless saved-card charge: Octo settled server-side without
            // a redirect, so there is no payment page and no return event. The
            // generic navigation effect would route to the order page without
            // finalizing, leaving the cart intact — finalize success here.
            this.onPaymentSuccess(octoOrderId);
          } else {
            this.recordPaymentLink('octo', paymentLink);
          }

          this.setIsLoading(false);
          this.setOrderId(octoOrderId);
          return response;
        }

        if (this.activePaymentSystem === PaymentSystem.moneta) {
          const order = response.data.orders[0]?.order;
          // The Moneta payment link is now created by `orders/new` and returned
          // inline (like Octo) — no separate `payments/create` call needed.
          const paymentLink =
            response.data.orders[0]?.payment?.providerPaymentDetails
              ?.paymentLink;

          this.recordPaymentLink('moneta', paymentLink);

          if (paymentLink) {
            this.monetaPaymentLink = paymentLink;
            if (mainStore.isRN) {
              this.openPaymentInAppBrowser('moneta', paymentLink);
            }
          }

          this.setIsLoading(false);
          this.setOrderId(order?.id ?? '');
          return response;
        }

        if (
          this.activePaymentSystem === PaymentSystem.midtrans &&
          response.data.orders[0]?.payment.midtransPayment
        ) {
          trace(DOMAIN.payment, 'Starting Midtrans payment', {
            stage: 'midtrans_payment',
            token: response.data.orders[0]?.payment.midtransPayment?.token,
          });
          this.midtransPay(
            response.data.orders[0].payment.midtransPayment?.token,
          );
          return;
        }

        if (
          this.activePaymentSystem === PaymentSystem.amazon &&
          this.activePaymentMethod === ETAPaymentMethod.Card
        ) {
          trace(DOMAIN.payment, 'Starting Amazon payment', {
            stage: 'amazon_payment',
            orderId: response.data.orders[0]?.order?.id,
          });
          this.setIsLoading(false);
          return;
        }

        if (!response.data.orders[0]?.payment_intent) {
          reportFailure(DOMAIN.payment, 'Missing payment intent', {
            reason: REASON.payment.missingIntent,
            extras: {
              stage: 'payment_intent_missing',
              error: 'no_payment_intent',
              orderId: response.data.orders[0]?.order?.id,
            },
          });
          this.setIsLoading(false);
          throw new Error('!response.data.orders[0]?.payment_intent');
        }

        if (this.activePaymentSystem === PaymentSystem.verifone) {
          // For wallet payments with Payment Request API, finalize immediately
          if (
            walletPaymentResult &&
            (this.activePaymentMethod === MobilePaymentMethod.ApplePay ||
              this.activePaymentMethod === MobilePaymentMethod.GooglePay)
          ) {
            trace(DOMAIN.payment, 'Finalizing Verifone wallet payment', {
              stage: 'verifone_wallet_payment_finalize',
              paymentMethod: this.activePaymentMethod,
              orderId: response.data.orders[0]?.order?.id,
            });

            await this.finalizePayment().catch(
              (error) => error && console.error(error),
            );
            this.setIsLoading(false);
            return;
          }

          // For card payments, return response for further processing
          if (
            this.activePaymentMethod === ETAPaymentMethod.Card &&
            (checkoutStore.isAddNewCard || !checkoutStore.paymentsMethodId)
          ) {
            return response;
          }
        }

        if (
          this.activePaymentSystem === PaymentSystem.adyen &&
          response.data.orders[0].payment.providerPaymentDetails
        ) {
          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.ApplePay
          ) {
            this.applePay(response);
            return;
          }

          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.GooglePay
          ) {
            this.googlePay(response);
            return;
          }

          if (
            this.activePaymentMethod === ETAPaymentMethod.Card &&
            (this.isAddNewCard || !this.paymentsMethodId)
          ) {
            return response;
          }
        }

        if (this.activePaymentSystem === PaymentSystem.stripe) {
          if (!response.data.orders[0]?.payment_intent) {
            reportFailure(DOMAIN.payment, 'Missing Stripe payment intent', {
              reason: REASON.payment.stripeMissingIntent,
              extras: {
                stage: 'stripe_payment_intent_missing',
                error: 'no_payment_intent',
                orderId: response.data.orders[0]?.order?.id,
              },
            });
            this.setIsLoading(false);
            throw new Error('!response.data.orders[0]?.payment_intent');
          }

          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.ApplePay
          ) {
            this.applePay(response);
            return;
          }

          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.GooglePay &&
            mainStore.isGooglePay
          ) {
            this.googlePay(response);
            return;
          }

          if (
            this.activePaymentMethod === ETAPaymentMethod.Card &&
            this.activePaymentSystem === PaymentSystem.stripe
          ) {
            if (!stripe || !elements) {
              reportFailure(DOMAIN.payment, 'Stripe not initialized', {
                reason: REASON.payment.stripeNotInitialized,
                extras: {
                  stage: 'stripe_initialization_error',
                  error: 'stripe_not_found',
                  hasStripe: !!stripe,
                  hasElements: !!elements,
                },
              });
              throw new Error('Stripe not found');
            }
            let paymentMethod: { card: StripeCardElement } | string;
            if (this.payments && this.payments.length && !this.isAddNewCard) {
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              paymentMethod = this.paymentsMethodId || this.payments[0].value;
            } else {
              paymentMethod = {
                card: elements.getElement(CardElement) as StripeCardElement,
              };
            }

            trace(DOMAIN.payment, 'Starting Stripe card payment', {
              stage: 'stripe_card_payment',
              orderId: response.data.orders[0]?.order?.id,
              isNewCard: this.isAddNewCard,
              hasExistingPayments: !!this.payments?.length,
            });

            this.setPaymentMethod(paymentMethod);
            this.stripeConfirmCardPayment(response, paymentMethod, stripe);
            return;
          }
        }
      }

      trace(DOMAIN.payment, 'Processing cash payment', {
        stage: 'cash_payment',
        orderId: response.data.orders[0]?.order?.id,
      });

      this.setPaymentMethod('');
      this.finalizePayment().catch((error) => error && console.error(error));
      return response;
    } catch (e) {
      if (e instanceof OrderErrorHandled) {
        checkoutAttempt.finish('failed', { handled: true });
        this.setIsLoading(false);
        return null;
      }

      reportFailure(DOMAIN.checkout, 'Checkout process failed', {
        reason: REASON.checkout.processFailed,
        extras: {
          stage: 'checkout_error',
          error: e,
          context: 'orderCheckout',
        },
      });
      checkoutAttempt.finish('failed', {
        handled: false,
        error: e instanceof Error ? e.message : String(e),
      });
      if (
        !this.isAddNewCard &&
        this.activePaymentSystem &&
        [PaymentSystem.verifone, PaymentSystem.pvit].includes(
          this.activePaymentSystem,
        )
      ) {
        const errorInstance = e as AxiosError<ApiErrorResponse>;
        mainStore.pushAlert('error', errorInstance.message);
      }

      throw new Error(
        e instanceof Error ? e.message : 'Unable to create order',
      );
    }
  }

  /**
   * Opens the Midtrans payment sheet, loading its SDK first if the deferred tag
   * has not delivered it yet.
   *
   * The order already exists on the backend by the time this runs, so failing
   * silently here leaves a paid-for-nothing order and a user staring at a
   * checkout that did nothing. Every exit therefore either opens the sheet or
   * says so, out loud and in Sentry.
   */
  async midtransPay(snapToken: string) {
    const setIsLoading = this.setIsLoading.bind(this);
    const finalizePayment = this.finalizePayment.bind(this);

    try {
      if (typeof window.snap?.pay !== 'function') {
        if (typeof window.__loadSnap !== 'function') {
          throw new Error('Midtrans Snap tag is missing from the document');
        }
        await window.__loadSnap();
      }

      window.snap?.pay(snapToken, {
        onSuccess: () => {
          finalizePayment().then(() => {
            const storedPromocode = getStoredPromocode();
            if (
              cartStore.promocode.success &&
              cartStore.promocode.value === storedPromocode?.code
            ) {
              clearStoredPromocode();
            }
          });
        },
        onError: (result) => {
          window.snap?.hide();
          mainStore.pushAlert('error', t('errors:unknown'));
          setIsLoading(false);
          reportFailure(DOMAIN.payment, 'Midtrans payment failed', {
            reason: REASON.payment.midtransFailed,
            extras: { result, snapToken },
          });
        },
        onClose: () => {
          setIsLoading(false);
        },
      });
    } catch (e) {
      setIsLoading(false);
      mainStore.pushAlert('error', t('errors:unknown'));
      reportError(DOMAIN.payment, e, {
        reason: REASON.payment.midtransFailed,
        extras: { snapToken },
      });
    }
  }

  async applePay(newOrderResponse: NewOrderResponse) {
    trace(DOMAIN.payment, 'Starting Apple Pay flow', {
      flow: 'apple_pay',
      orderId: newOrderResponse.data.orders[0]?.order?.id,
      paymentSystem: this.activePaymentSystem,
      orderAmount: cartStore.finalPrice,
      currency: orderStore.currency,
      cartItems: cartStore.cart.map((item) => ({
        sku: item.sku,
        name: item.name,
        count: item.count,
        price: item.price,
      })),
      deliveryFee: this.calculateFullDeliveryFee(),
      paymentIntent: newOrderResponse.data.orders[0]?.payment_intent,
      myFatoorahDetails:
        newOrderResponse.data.orders[0]?.payment?.myFatoorahPayment,
    });

    const deliveryFee = this.calculateFullDeliveryFee();

    if (this.activePaymentSystem === PaymentSystem.myfatoorah) {
      mainStore.sendToRN(
        'initApplePay',
        {
          params: {
            cartItems: cartStore.cart
              .map<Record<string, string>>((item) => {
                return {
                  label: item.name || '',
                  paymentType: 'Immediate',
                  amount: mainStore.convertPenceToPounds(
                    (item.discountPrice ? item.discountPrice : item.price) *
                      item.count,
                  ),
                };
              })
              .concat({
                label: company.name,
                amount: cartStore.finalPrice,
                paymentType: 'Immediate',
              }),
            country: company.config.countryCode || 'GB',
            currency: orderStore.currency.toUpperCase(),
            shippingMethods: [
              {
                label: 'Delivery',
                amount: deliveryFee || '0',
                identifier: 'delivery',
              },
            ],
          },
          clientSecret: undefined,
        },
        (e) => {
          trace(DOMAIN.payment, 'Apple Pay succeeded', {
            response: e,
            paymentAmount: cartStore.finalPrice,
          });
        },
        (e) => {
          reportWarning(DOMAIN.payment, 'Apple Pay failed', {
            reason: REASON.payment.applePayFailed,
            extras: {
              error: e,
              paymentAmount: cartStore.finalPrice,
            },
          });
        },
      );
    }

    await this.processPayment(newOrderResponse, deliveryFee);
  }

  /**
   * Releases the order behind a wallet payment, but only when it is certain
   * that no money moved.
   *
   * `canceled` is the one outcome that carries that certainty: the customer
   * dismissed the sheet, so no authorisation was ever requested and the order
   * is safe to void.
   *
   * `failed` and `timeout` look like good candidates and are not. Both can
   * arrive *after* a successful authorisation — a wallet that charged the card
   * and then lost the connection carrying the result back into the WebView
   * reports a failure, and a native layer that goes quiet does so just as
   * readily once the payment has gone through. Cancelling there would void an
   * order the customer has paid for, which is far worse than the alternative:
   * the order sits in `reserved` until the backend's own timer reclaims it,
   * and the backend is the only side that knows whether money actually moved.
   *
   * The visible cost of that restraint is the retry loop it permits — a
   * customer who sees a failure pays again, and each attempt mints a new order.
   * `checkout/order_created` and `payment/stalled` exist to measure exactly
   * that, so the decision can be revisited with numbers rather than guesses.
   */
  private async releaseUnpaidOrder(
    orderId: string,
    paymentResult: 'failed' | 'canceled' | 'timeout',
  ) {
    if (paymentResult !== 'canceled') {
      return;
    }

    await orderStore
      .orderCancel(orderId)
      .catch((error) => error && console.error(error));
  }

  async processPayment(
    newOrderResponse: NewOrderResponse,
    deliveryFee: string,
  ) {
    trace(DOMAIN.payment, 'Processing payment', {
      stage: 'process_payment_start',
      orderId: newOrderResponse.data.orders[0]?.order?.id,
      paymentSystem: this.activePaymentSystem,
      paymentMethod: this.activePaymentMethod,
      orderAmount: cartStore.finalPrice,
      currency: orderStore.currency,
      paymentIntent: newOrderResponse.data.orders[0]?.payment_intent,
      clientSecret:
        newOrderResponse.data.orders[0]?.payment_intent?.client_secret,
      deliveryFee,
      timestamp: new Date().toISOString(),
    });

    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    if (
      !order.payment_intent ||
      !order.payment_intent.client_secret ||
      !order.order
    ) {
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      this.setIsLoading(false);
      throw new Error('!order.payment_intent');
    }

    try {
      const paymentResult = await new Promise<
        'success' | 'failed' | 'canceled' | 'timeout'
      >((resolve) => {
        if (!order.payment_intent) {
          reportFailure(DOMAIN.payment, 'Missing payment intent', {
            reason: REASON.payment.missingIntent,
            extras: {
              stage: 'payment_intent_missing',
              orderId: order.order?.id,
            },
          });
          resolve('failed');
          return;
        }

        const timeout = setTimeout(() => {
          reportFailure(DOMAIN.payment, 'Payment timed out', {
            reason: REASON.payment.timedOut,
            extras: {
              stage: 'payment_timeout',
              orderId: order.order?.id,
              timeout: PAYMENT_TIMEOUT / 1000 + 'sec',
            },
          });
          resolve('timeout');
        }, PAYMENT_TIMEOUT);

        /**
         * Says out loud that the wallet has gone quiet, without touching the
         * flow — the payment keeps waiting for its real timeout.
         *
         * That timeout is ten minutes, which is a reasonable ceiling and
         * worthless as evidence: by the time it fires the customer has usually
         * given up and started again, every retry replaces `paymentIntent`, and
         * the stale attempt then fails the `client_secret` comparison below and
         * reports nothing. This is the report that actually arrives, while the
         * attempt it describes is still the live one.
         */
        const stallReport = setTimeout(() => {
          reportWarning(DOMAIN.payment, 'Wallet payment has not returned', {
            reason: REASON.payment.stalled,
            extras: {
              stage: 'payment_stalled',
              orderId: order.order?.id,
              waitedFor: PAYMENT_STALL_REPORT_AFTER / 1000 + 'sec',
              paymentMethod: this.activePaymentMethod,
              paymentSystem: this.activePaymentSystem,
            },
          });
        }, PAYMENT_STALL_REPORT_AFTER);

        mainStore.sendToRN(
          'initApplePay',
          {
            params: {
              cartItems: cartStore.cart
                .map<Record<string, string>>((item) => {
                  return {
                    label: item.name || '',
                    amount: mainStore.convertPenceToPounds(
                      (item.discountPrice ? item.discountPrice : item.price) *
                        item.count,
                    ),
                    paymentType: 'Immediate',
                  };
                })
                .concat({
                  label: company.name,
                  amount: cartStore.finalPrice,
                  paymentType: 'Immediate',
                }),
              country: company.config.countryCode || 'GB',
              currency: orderStore.currency.toUpperCase(),
              shippingMethods: [
                {
                  label: 'Delivery',
                  amount: deliveryFee ?? '0',
                  identifier: 'delivery',
                },
              ],
            },
            paymentMethods: orderStore.paymentMethods,
            sessionData: order.payment.providerPaymentDetails,
            clientSecret: order.payment_intent.client_secret,
          },
          (e) => {
            clearTimeout(timeout);
            clearTimeout(stallReport);
            trace(DOMAIN.payment, 'Apple Pay succeeded', {
              response: e,
              paymentAmount: cartStore.finalPrice,
            });
            resolve('success');
          },
          (e) => {
            clearTimeout(timeout);
            clearTimeout(stallReport);
            reportWarning(DOMAIN.payment, 'Apple Pay failed', {
              reason: REASON.payment.applePayFailed,
              extras: {
                error: e,
                paymentAmount: cartStore.finalPrice,
              },
            });
            const isCanceled =
              e.error?.code === 'Canceled' ||
              e.error?.code === 'Cancelled' ||
              e.error?.errorCode === 'canceledByShopper';
            resolve(isCanceled ? 'canceled' : 'failed');
          },
        );
      });

      if (
        this.paymentIntent?.payment_intent?.client_secret !==
        order.payment_intent.client_secret
      ) {
        throw new Error(
          'this.paymentIntent?.payment_intent?.client_secret !== order.payment_intent.client_secret',
        );
      } else {
        if (paymentResult === 'success') {
          await this.finalizePayment().catch(
            (error) => error && console.error(error),
          );
        } else {
          await this.releaseUnpaidOrder(order.order.id, paymentResult);
          if (paymentResult === 'failed' || paymentResult === 'timeout') {
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            throw new Error('failed or timeout');
          }
        }
      }
      this.setIsLoading(false);
    } catch (error) {
      if (
        this.paymentIntent?.payment_intent?.client_secret ===
        order.payment_intent.client_secret
      ) {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
      }
      reportWarning(DOMAIN.payment, 'Apple Pay threw', {
        reason: REASON.payment.applePayException,
        extras: {
          error,
        },
      });
    }
  }

  async googlePay(newOrderResponse: NewOrderResponse) {
    trace(DOMAIN.payment, 'Starting Google Pay flow', {
      flow: 'google_pay',
      orderId: newOrderResponse.data.orders[0]?.order?.id,
      paymentSystem: this.activePaymentSystem,
      orderAmount: cartStore.finalPrice,
      currency: orderStore.currency,
      cartItems: cartStore.cart.map((item) => ({
        sku: item.sku,
        name: item.name,
        count: item.count,
        price: item.price,
      })),
      paymentIntent: newOrderResponse.data.orders[0]?.payment_intent,
      clientSecret:
        newOrderResponse.data.orders[0]?.payment_intent?.client_secret,
    });

    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    if (
      !order.payment_intent ||
      !order.payment_intent.client_secret ||
      !order.order
    ) {
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      this.setIsLoading(false);
      throw new Error('!order.payment_intent');
    }
    try {
      const paymentResult = await new Promise<
        'success' | 'failed' | 'canceled' | 'timeout'
      >((resolve) => {
        if (!order.payment_intent) {
          resolve('failed');
          return;
        }
        const timeout = setTimeout(() => {
          reportWarning(DOMAIN.payment, 'Google Pay timed out', {
            reason: REASON.payment.googlePayTimeout,
            extras: {
              timeout: PAYMENT_TIMEOUT / 1000 + 'sec',
              request: {},
            },
          });
          resolve('timeout');
        }, PAYMENT_TIMEOUT);
        // Same reasoning as the Apple Pay path: the ten-minute timeout is the
        // ceiling, this is the one that reports in time to be useful.
        const stallReport = setTimeout(() => {
          reportWarning(DOMAIN.payment, 'Wallet payment has not returned', {
            reason: REASON.payment.stalled,
            extras: {
              stage: 'payment_stalled',
              orderId: order.order?.id,
              waitedFor: PAYMENT_STALL_REPORT_AFTER / 1000 + 'sec',
              paymentMethod: this.activePaymentMethod,
              paymentSystem: this.activePaymentSystem,
            },
          });
        }, PAYMENT_STALL_REPORT_AFTER);
        mainStore.sendToRN(
          'requestGooglePayment',
          {
            transaction: {
              totalPrice: cartStore.finalPrice,
              totalPriceStatus: 'FINAL',
              currencyCode: orderStore.currency.toUpperCase(),
            },
            merchantName: this.isRecipientValid
              ? this.recipientNameVal
              : this.userStore.fullName,
            clientSecret: order.payment_intent.client_secret,
            sessionData: order.payment.providerPaymentDetails,
            paymentMethods: orderStore.paymentMethods,
          },
          (e) => {
            clearTimeout(timeout);
            clearTimeout(stallReport);
            trace(DOMAIN.payment, 'Google Pay succeeded', {
              response: e,
              paymentAmount: cartStore.finalPrice,
            });
            resolve('success');
          },
          (e) => {
            clearTimeout(timeout);
            clearTimeout(stallReport);
            reportWarning(DOMAIN.payment, 'Google Pay failed', {
              reason: REASON.payment.googlePayFailed,
              extras: {
                error: e,
                paymentAmount: cartStore.finalPrice,
              },
            });
            const isCanceled =
              e.confirmationError?.code === 'PAYMENT_RESULT_CANCELED' ||
              e.error?.code === 'Canceled' ||
              e.error?.code === 'Cancelled' ||
              e.error?.errorCode === 'canceledByShopper';
            resolve(isCanceled ? 'canceled' : 'failed');
          },
        );
      });
      if (
        this.paymentIntent?.payment_intent?.client_secret !==
        order.payment_intent.client_secret
      ) {
        throw new Error(
          'this.paymentIntent?.payment_intent?.client_secret !== order.payment_intent.client_secret',
        );
      } else {
        if (paymentResult === 'success') {
          await this.finalizePayment().catch(
            (error) => error && console.error(error),
          );
        } else {
          await this.releaseUnpaidOrder(order.order.id, paymentResult);
          if (paymentResult === 'failed' || paymentResult === 'timeout') {
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            throw new Error('failed or timeout');
          }
        }
      }
      this.setIsLoading(false);
    } catch (error) {
      if (
        this.paymentIntent?.payment_intent?.client_secret ===
        order.payment_intent.client_secret
      ) {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
      }
      reportWarning(DOMAIN.payment, 'Google Pay threw', {
        reason: REASON.payment.googlePayException,
        extras: {
          error,
        },
      });
    }
  }

  async stripeConfirmCardPayment(
    newOrderResponse: NewOrderResponse,
    paymentMethod: { card: StripeCardElement } | string,
    stripe: Stripe,
  ) {
    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    if (
      !order.payment_intent ||
      !order.payment_intent.client_secret ||
      !order.order
    ) {
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      this.setIsLoading(false);
      throw new Error('!order.payment_intent');
    }
    try {
      const paymentResult = await new Promise<
        PaymentIntentResult | 'failed' | 'timeout'
      >((resolve, reject) => {
        if (!order.payment_intent) {
          resolve('failed');
          return;
        }

        const timeoutFroze = setTimeout(() => {
          reportWarning(
            DOMAIN.payment,
            'Stripe confirmCardPayment timeout 10min',
            {
              reason: REASON.payment.stripeConfirmTimeout,
              extras: {
                timeout: PAYMENT_TIMEOUT / 1000 + 'sec',
                request: {
                  paymentMethod,
                },
              },
            },
          );
          resolve('timeout');
        }, PAYMENT_TIMEOUT);

        stripe
          .confirmCardPayment(order.payment_intent.client_secret || '', {
            payment_method: paymentMethod,
          })
          .then((e) => {
            clearTimeout(timeoutFroze);
            resolve(e);
          })
          .catch((e) => {
            clearTimeout(timeoutFroze);
            reject(e);
          });
      });

      if (
        this.paymentIntent?.payment_intent?.client_secret !==
        order.payment_intent.client_secret
      ) {
        throw new Error(
          'paymentIntent?.payment_intent?.client_secret !== order.payment_intent.client_secret',
        );
      }

      if (paymentResult === 'failed' || paymentResult === 'timeout') {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
        throw new Error('Invalid paymentResult');
      }

      if (paymentResult.error) {
        this.setError(paymentResult.error.message || '');
        reportWarning(DOMAIN.payment, 'Stripe confirmCardPayment error', {
          reason: REASON.payment.stripeConfirmError,
          extras: {
            response: paymentResult,
          },
        });
        this.setIsLoading(false);
        return;
      }

      await this.finalizePayment();
      this.setIsLoading(false);
    } catch (error) {
      if (
        this.paymentIntent?.payment_intent?.client_secret ===
        order.payment_intent.client_secret
      ) {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
      }
      reportWarning(DOMAIN.payment, 'Stripe confirmCardPayment exception', {
        reason: REASON.payment.stripeConfirmException,
        extras: {
          error,
        },
      });
    }
  }

  private recordPaymentLink(provider: string, paymentLink?: string | null) {
    if (!paymentLink) {
      checkoutAttempt.step('payment_link_missing', { provider });
      return;
    }
    checkoutAttempt.step('payment_link_received', {
      provider,
      isValid: isHttpsUrl(paymentLink),
      url: sanitizeUrl(paymentLink),
    });
  }

  private openPaymentInAppBrowser(provider: string, paymentLink: string) {
    mainStore.setInAppBrowserClosedAt(0);
    mainStore.sendToRN(
      'openInAppBrowser',
      { url: paymentLink },
      undefined,
      () => checkoutAttempt.step('rn_open_in_app_browser_failed', { provider }),
    );
    checkoutAttempt.step('rn_open_in_app_browser_sent', {
      provider,
      hasBridge: !!window.ReactNativeWebView,
    });
  }

  onPaymentSuccess(orderId: string) {
    if (this._processedOrderIds.has(orderId)) return;
    this._processedOrderIds.add(orderId);
    checkoutAttempt.finish('paid', { orderId });

    const wasFirstOrder = orderStore.isFirstOrder;

    orderStore
      .updateOrdersAnalytics()
      .catch((error) => error && console.error(error));
    orderStore.setOrderStatus('accepted');
    orderStore.setIsFirstOrder(false);
    mainStore.setNewDeliveryMethodForPromocodePopover(null);
    orderStore.setActiveOrderProductCount(cartStore.totalCartCount);
    orderStore.setActiveOrderID(orderId);
    // Promocode was entered AND accepted by /calculate (works for both legacy and simplified flows).
    if (cartStore.promocode.value && cartStore.promocode.success) {
      const promocode = cartStore.promocode.value.toUpperCase();
      if (
        PRESENT_PROMOCODES.includes(promocode) ||
        cartStore.promocode.discountLimit
      ) {
        cartStore.promotionStore.addUsedPromocodes(promocode);
      }
      mainStore.analytics.promocodeUsedCount += 1;
      mainStore.sendToRN('setUserProperties', {
        'Commerce: promocodes used': mainStore.analytics.promocodeUsedCount,
      });
    }
    this.purchaseCompletedAnalytics(orderId, wasFirstOrder);
    orderStore.setActiveGift(null);
    orderStore.setGift(null);
    cartStore.emptySelectedItemsFromCart();
    // `deliveries` is persisted, so a self-collection leg would carry the finished
    // order's window into the next cart and `_buildPickupDelivery` would keep it as
    // the customer's standing choice. Courier legs are rebuilt from the slot pool and
    // are left alone.
    if (this.isPickupSelected) {
      this.setDeliveries(null);
    }
    this.setSeletectedDateExpress();
    cartStore.promotionStore.resetPromocode();
    this.setUseBonuses(false);
  }

  /**
   * Finalize the Octo payment flow regardless of how the gateway returned
   * (web iframe `postMessage`, RN deeplink, or order-status polling).
   *
   * Closes the in-app browser overlay (RN) and clears the iframe state. When
   * the gateway reports a cancelled/failed result the cart is kept intact and
   * the user stays on checkout to retry; on success post-payment analytics run
   * and the app navigates to the order page. Without an `orderId` only the
   * overlay teardown happens.
   *
   * @param orderId Order id returned by Octo, if any
   * @param status Result status: gateway string (`canceled`/`failed`) or the
   *               polled order status (`cancelled`) marks a failed payment
   * @param navigate React Router navigate function from the caller's scope
   */
  completeOctoPayment(
    orderId: string | undefined,
    status: string | undefined,
    navigate: NavigateFunction,
  ) {
    if (mainStore.isRN) {
      mainStore.sendToRN('closeInAppBrowser');
    }
    this.octoPaymentLink = null;

    if (!orderId) {
      return;
    }

    // A cancelled/failed gateway result must not be finalized as a paid order:
    // keep the cart and let the user retry from checkout with a fresh
    // idempotency key instead of navigating to a dead order page.
    if (
      status === 'cancelled' ||
      status === 'canceled' ||
      status === 'failed'
    ) {
      checkoutAttempt.finish(status === 'failed' ? 'failed' : 'cancelled', {
        orderId,
        status,
      });
      purchaseRecovery.forget(orderId);
      this.setOrderId('');
      return;
    }

    this.onPaymentSuccess(orderId);
    navigate(
      // A cart split across partner warehouses becomes several orders, and
      // opening one of them would hide the rest.
      company.shouldNavigateToOrdersPageAfterOrder ||
        this.createdOrdersCount > 1
        ? '/cabinet/orders'
        : `/order/${orderId}`,
      {
        replace: true,
        state: { linkBack: '/' },
      },
    );
  }

  async finalizePayment() {
    const isPostpayment =
      this.activePaymentSystem === PaymentSystem.postpayment;

    if (
      !this.paymentIntent ||
      !this.paymentIntent.order ||
      (!isPostpayment && !this.paymentIntent.payment_intent)
    ) {
      reportFailure(
        DOMAIN.payment,
        'Cannot finalize payment - missing payment intent',
        {
          reason: REASON.payment.finalizeMissingIntent,
          extras: {
            stage: 'finalize_payment_start',
            error: 'missing_payment_intent',
            paymentIntent: this.paymentIntent,
            orderState: {
              hasOrder: !!this.paymentIntent?.order,
              hasPaymentIntent: !!this.paymentIntent?.payment_intent,
              orderId: this.paymentIntent?.order?.id || null,
            },
          },
        },
      );
      throw new Error('!this.paymentIntent');
    }

    const isCardPayment = (
      [
        ETAPaymentMethod.Card,
        AdditionalPaymentMethod.VisaMastercard,
      ] as PaymentMethod[]
    ).includes(this.activePaymentMethod || ETAPaymentMethod.Card);

    try {
      this.setError(null);
      trace(DOMAIN.payment, 'Starting payment verification', {
        stage: 'payment_verification_start',
        orderId: this.paymentIntent?.order?.id || null,
        paymentSystem: this.activePaymentSystem,
        isCardPayment,
        paymentMethod: this.activePaymentMethod,
      });

      let count =
        (this.activePaymentSystem === PaymentSystem.myfatoorah ||
          this.activePaymentSystem === PaymentSystem.adyen ||
          this.activePaymentSystem === PaymentSystem.amazon ||
          this.activePaymentSystem === PaymentSystem.verifone ||
          this.activePaymentSystem === PaymentSystem.octo) &&
        isCardPayment
          ? 60
          : 6;
      let bonuses = null;
      const waitingExpires = Date.now() + PAYMENT_WAITING_TIME * 1000;
      while (count > 0) {
        this.setIsLoading(true);
        if (!this.paymentIntent || !this.paymentIntent.order) {
          mainStore.sendToRN('closeInAppBrowser');
          this.setPaymentIntent(null);
          this.setError('' /*i18n.t('errors:paymentSystemError')*/);
          this.setIsLoading(false);
          throw new Error('!this.paymentIntent || !this.paymentIntent.order');
        }
        if (
          this.activePaymentSystem === PaymentSystem.myfatoorah &&
          isCardPayment &&
          Date.now() >= waitingExpires
        ) {
          mainStore.sendToRN('closeInAppBrowser');
          this.setPaymentIntent(null);
          this.setError('' /*i18n.t('errors:paymentSystemError')*/);
          this.setIsLoading(false);
          throw new Error('waiting expires');
        }
        const { address, status, received_bonuses } =
          await orderStore.checkOrderPayment(this.paymentIntent.order.id);

        // Where the destination is chosen at checkout the store already holds
        // what the customer picked, while the order carries the destination's
        // own address — a pickup point, or a street line without city and
        // building. Writing that back would overwrite their address with it.
        if (address && !company.addressSelectedAtCheckout) {
          if (
            address.customer_address_id !==
            this.userStore.deliveryAddress?.addressId
          ) {
            this.userStore.setDeliveryAddress({
              addressId: address.customer_address_id,
              zip: address.zip,
              country: address.country_id || '',
              city: address.city,
              region: address.region,
              address1: address.address_1 || '',
              address2: address.address_2 ?? '',
              addressFloor: address.floor || '',
              addressBld: address.building || '',
              addressApt: address.apartment || '',
              type:
                address.type ||
                (company.canDefaultAddressBeEmpty ? '' : 'home'),
              shortAddress: address.address_1 || '',
              coordinates: {
                lat: address.latitude || 0,
                lng: address.longitude || 0,
              },
              placeId: address.place_id || '',
              comment: address.comment || '',
              instructions: address.instructions || [],
            });
          }
        }

        trace(DOMAIN.payment, 'Payment verification check', {
          stage: 'payment_verification_check',
          orderId: this.paymentIntent?.order?.id || null,
          status,
          attemptsLeft: count,
          received_bonuses,
          timeUntilExpiry: waitingExpires - Date.now(),
        });

        bonuses = received_bonuses;
        count--;
        if (!status || ['created', 'reserved'].includes(status)) {
          if (
            this.activePaymentSystem === PaymentSystem.pvit ||
            isPostpayment
          ) {
            break;
          }

          if (count) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          } else {
            reportFailure(
              DOMAIN.payment,
              'Payment verification failed - max retries reached',
              {
                reason: REASON.payment.verificationFailed,
                extras: {
                  stage: 'payment_verification_failed',
                  orderId: this.paymentIntent?.order?.id || null,
                  finalStatus: status,
                  error: 'max_retries_reached',
                },
              },
            );
            mainStore.sendToRN('closeInAppBrowser');
            this.setPaymentIntent(null);
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            this.setIsLoading(false);
            throw new Error('!status');
          }
        }

        if (status === 'cancelled') {
          const cancelledOrderId = this.paymentIntent?.order?.id;

          reportWarning(DOMAIN.payment, 'Payment cancelled by user or system', {
            reason: REASON.payment.cancelled,
            extras: {
              stage: 'payment_cancelled',
              orderId: cancelledOrderId || null,
              status,
            },
          });
          mainStore.sendToRN('closeInAppBrowser');
          this.setPaymentIntent(null);
          this.setError('' /*i18n.t('errors:paymentSystemError')*/);
          this.setIsLoading(false);

          if (
            this.activePaymentSystem === PaymentSystem.pvit &&
            cancelledOrderId
          ) {
            this.setOrderId(cancelledOrderId);
            return;
          }

          throw new Error('cancelled');
        }
        break;
      }

      trace(DOMAIN.payment, 'Payment completed successfully', {
        stage: 'payment_success',
        orderId: this.paymentIntent?.order?.id || null,
        bonuses,
        finalPrice: cartStore.finalPrice,
        promocode: cartStore.promocode.value,
      });

      mainStore.sendToRN('closeInAppBrowser');
      this.onPaymentSuccess(this.paymentIntent.order.id);
      this.setOrderId(this.paymentIntent.order.id);
      orderStore.setActiveOrderBonuses(bonuses);
      if (!this.userStore.deliveryAddress?.address1) {
        this.userStore.setDeliveryAddress1(this.addressVal);
      }
      if (!this.userStore.deliveryAddress?.address2) {
        this.userStore.setDeliveryAddress2(this.address2Val);
      }
      if (!this.userStore.deliveryAddress?.addressFloor) {
        this.userStore.setDeliveryAddressFloor(this.addressFloorVal);
      }
      if (!this.userStore.deliveryAddress?.addressBld) {
        this.userStore.setDeliveryAddressBld(this.addressBldVal);
      }
      if (!this.userStore.deliveryAddress?.addressApt) {
        this.userStore.setDeliveryAddressApt(this.addressAptVal);
      }
    } catch (e) {
      this.setIsLoading(false);
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      throw e;
    }
  }

  async syncAddress() {
    try {
      if (this.userStore.deliveryAddress) {
        let isChangedAddress = false;
        if (this.commentVal !== this.userStore.deliveryAddress.comment) {
          isChangedAddress = true;
          runInAction(() => {
            if (!this.userStore.deliveryAddress) {
              return;
            }
            this.userStore.deliveryAddress.comment = this.commentVal;
          });
        }
        if (
          Object.values(this.instructionList).length !==
          this.userStore.deliveryAddress.instructions?.length
        ) {
          runInAction(() => {
            if (!this.userStore.deliveryAddress) {
              return;
            }
            if (
              cartStore.isAdultItemInCart &&
              Object.values(this.instructionList).length === 2 &&
              this.userStore.deliveryAddress.instructions?.length === 3
            ) {
              return;
            }
            isChangedAddress = true;
            this.userStore.deliveryAddress.instructions = Object.values(
              this.instructionList,
            );
          });
        }
        if (!this.userStore.deliveryAddress.addressId) {
          await this.addNewAddress().catch(
            (error) => error && console.error(error),
          );
        } else {
          if (isChangedAddress) {
            const isSuccess = await this.userStore
              .updateAddress(this.userStore.deliveryAddress.addressId, {
                comment: this.userStore.deliveryAddress.comment || '',
                instructions: this.userStore.deliveryAddress.instructions || [],
              })
              .catch((error) => error && console.error(error));
            if (isSuccess) {
              return;
            }
            await this.addNewAddress().catch(
              (error) => error && console.error(error),
            );
          }
        }
      }
    } catch (error) {
      error && console.error(error);
    }
  }

  async addNewAddress() {
    if (!this.userStore.deliveryAddress) {
      return;
    }
    try {
      const address = await this.userStore
        .addAddress({
          street_address_1: this.userStore.deliveryAddress.address1,
          street_address_2: this.userStore.deliveryAddress.address2,
          building: this.userStore.deliveryAddress.addressBld,
          floor: this.userStore.deliveryAddress.addressFloor,
          apartment: this.userStore.deliveryAddress.addressApt,
          city: this.userStore.deliveryAddress.city,
          postcode: this.userStore.deliveryAddress.zip,
          latitude: this.userStore.deliveryAddress.coordinates.lat,
          longitude: this.userStore.deliveryAddress.coordinates.lng,
          country: this.userStore.deliveryAddress.country,
          comment: this.userStore.deliveryAddress.comment || '',
          instructions: this.userStore.deliveryAddress.instructions || [],
          type:
            this.userStore.deliveryAddress.type ||
            (company.canDefaultAddressBeEmpty ? '' : 'home'),
          ...partnerPickupAddressRequest(
            this.userStore.deliveryAddress,
            company.partnerPickupDeliveryProvider,
          ),
        })
        .catch((error) => error && console.error(error));
      runInAction(() => {
        if (!this.userStore.deliveryAddress) {
          return;
        }
        this.userStore.deliveryAddress.addressId = address.id || null;
        this.userStore.setDeliveryAddress2(address.street_address_2);
      });
    } catch (error) {
      error && console.error(error);
    }
  }

  resetStore(opts?: { resetDeliveryMethod?: boolean }) {
    this.addressVal = '';
    this.phoneVal = '';
    this.isPhoneValid = true;
    this.isEmailSync = true;
    this.isPersonalDataSync = true;
    this.nameVal = '';
    this.emailVal = '';
    this.commentVal = '';
    this.recipientPhoneVal = '';
    this.isRecipientPhoneValid = false;
    this.recipientNameVal = '';
    this.recipientEmailVal = '';
    this.recipientTempPhoneVal = '';
    this.isRecipientTempPhoneValid = false;
    this.recipientTempNameVal = '';
    this.recipientTempEmailVal = '';
    this.agreeSaveCard = true;
    this.error = null;
    this.isLoading = false;
    this.isDisabled = true;
    this.isPersonalDataChanged = false;
    this.isShowRecipientPopover = false;
    this.isShowTimeoutPopover = false;
    this.isFarFromDeliveryAddress = false;
    this.isUnableConfirmCardPayment = false;
    this.isAddNewCard = false;
    this.arePaymentsLoaded = false;
    this._isCardValid = false;
    this._isCardAdded = false;
    this.paymentIntent = null;
    this.paymentMethod = '';
    this.orderId = '';
    this.createdOrdersCount = 0;
    this.octoPaymentLink = null;
    this.monetaPaymentLink = null;
    this.cancellationDelay = null;
    this.isThreeds2Active = false;
    this.instructionList = {};
    this.deliverEarlier = false;
    this.stateData = null;
    this.isRefreshed = false;
    this.isUpdatedDeliveryPopover = false;
    this.sessionId = undefined;
    this.isDeliverTogether = company.combineItemsByDefaut;
    this._isNewAdyenCard = null;
    this._isAdyenCardValid = null;

    const { resetDeliveryMethod = false } = opts || {};

    if (resetDeliveryMethod) {
      if (
        !this.availableDeliveryMethods.length ||
        this.availableDeliveryMethods.includes(DeliveryMethods.COURIER_DELIVERY)
      ) {
        this.setDeliveryMethod(DeliveryMethods.COURIER_DELIVERY);
      } else {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        this.setDeliveryMethod(this.availableDeliveryMethods[0]);
      }
    }
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }

  private getExactAmountInstruction() {
    return 'Client will prepare the exact amount';
  }

  /**
   * Build cart items for wallet payments (Apple Pay, Google Pay)
   * Used for Payment Request API integration
   */
  private buildWalletCartItems() {
    return cartStore.cart
      .map<Record<string, string>>((item) => {
        return {
          label: item.name || '',
          amount: mainStore.convertPenceToPounds(
            (item.discountPrice ? item.discountPrice : item.price) * item.count,
          ),
          paymentType: 'Immediate',
        };
      })
      .concat({
        label: company.name,
        amount: cartStore.finalPrice,
        paymentType: 'Immediate',
      });
  }

  /**
   * Process wallet payment using Payment Request API
   * Returns the wallet payment result with token
   */
  private processWalletPaymentRequest(
    paymentMethod: 'applePay' | 'googlePay',
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const cartItems = this.buildWalletCartItems();
      const eventName =
        paymentMethod === 'applePay' ? 'initApplePay' : 'requestGooglePayment';

      const params =
        paymentMethod === 'applePay'
          ? {
              cartItems,
              country: company.config.countryCode || 'GB',
              currency: orderStore.currency.toUpperCase(),
              shippingMethods: [
                {
                  label: 'Delivery',
                  amount: 0 ?? '0',
                  identifier: 'delivery',
                },
              ],
            }
          : {
              cartItems,
              merchantName: company.name,
              country: company.config.countryCode || 'GB',
              currency: orderStore.currency.toUpperCase(),
            };

      mainStore.sendToRN(
        eventName,
        {
          params,
          usePaymentRequestAPI: true,
        },
        (response) => {
          trace(DOMAIN.payment, 'Wallet payment succeeded', {
            wallet: paymentMethod,
            response,
            paymentAmount: cartStore.finalPrice,
          });
          resolve(response);
        },
        (error) => {
          // One title for both wallets, split by fingerprint rather than by
          // interpolating the wallet into the name: the pair stays greppable as
          // one failure and still separates into two issues on the dashboard.
          reportWarning(DOMAIN.payment, 'Wallet payment failed', {
            reason:
              paymentMethod === 'applePay'
                ? REASON.payment.applePayFailed
                : REASON.payment.googlePayFailed,
            fingerprint: [paymentMethod],
            extras: { error, paymentAmount: cartStore.finalPrice },
          });
          reject(error);
        },
      );
    });
  }
}
